import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { ADMIN_DASHBOARD, getAvatarUrl } from '../../data/mockData';
import { apiClient } from '../../services';
import DashboardSkeleton from '../../components/DashboardSkeleton';
import { getCached, setCached } from '../../utils/dashboardCache';
import { useFeatureFlags } from '../../context/FeatureFlagContext';
import { useContent } from '../../context/ContentContext';
import { useIntegrations } from '../../context/IntegrationsContext';
import { useEmail } from '../../context/EmailContext';
import { COURSES, ALL_ENROLLMENTS, MEETING_PLATFORMS } from '../../features/certifications/data';
import { EVENTS } from '../../features/events/data';
import { PlatformIcon } from '../../components/MeetingJoinButton';

const D = ADMIN_DASHBOARD;

function Av({ seed, bg, init, size=36 }) {
  return (
    <div style={{width:size,height:size,borderRadius:9,overflow:'hidden',flexShrink:0,background:`#${bg}`,position:'relative',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <img src={getAvatarUrl(seed,bg,size)} alt={init} style={{width:'100%',height:'100%',objectFit:'cover'}} onError={e=>e.target.style.display='none'}/>
      <span style={{position:'absolute',fontWeight:800,fontSize:'.68rem',color:'#fff'}}>{init}</span>
    </div>
  );
}

function Overview({ stats: _stats, pendingMentors: _pm, recentBookings: _rb, onApprove, onReject }) {
  const isMock = process.env.REACT_APP_MOCK !== 'false';
  const nav = useNavigate();
  const { platformStats: S_mock, revenueByMonth, recentBookings: D_bookings } = D;
  // Use API data when available, fall back to mock
  const S  = _stats   || (isMock ? S_mock : {});
  // In mock mode, always re-read db_approvals_acted so approvals made in
  // the Approvals sub-page are immediately reflected here without a page reload
  const pm = (() => {
    const base = _pm || (isMock ? D.pendingMentors : []);
    if (!isMock) return base;
    try {
      const actedOn = JSON.parse(localStorage.getItem('db_approvals_acted') || '{}');
      return base.filter(m => !actedOn[m._id || m.id]);
    } catch { return base; }
  })();
  const recentBookings = _rb || (isMock ? D_bookings : []);
  return (
    <div>
      <h1 className="db-page-title">Admin Dashboard 🛠️</h1><p className="db-page-sub">Platform overview — real-time at a glance.</p>
      <div className="db-stats-grid-4" style={{marginBottom:24}}>
        {[{icon:'👨‍🏫',label:'Total Mentors',v:S.totalMentors,sub:`${S.pendingApproval} pending approval`,badge:S.pendingApproval},{icon:'🎓',label:'Total Mentees',v:S.totalMentees.toLocaleString(),sub:`${S.activeBookings} active bookings`},{icon:'📅',label:'Total Bookings',v:S.totalBookings.toLocaleString(),sub:'all time'},{icon:'💰',label:'Monthly Revenue',v:`₹${(S.monthRevenue/100000).toFixed(1)}L`,sub:`₹${(S.commissionEarned/100000).toFixed(1)}L commission earned`}].map(s=>(
          <div key={s.label} className="db-stat-card">
            {s.badge&&<div style={{position:'absolute',right:14,top:14}}><span className="st-badge st-open">{s.badge} new</span></div>}
            <div className="dsc-icon">{s.icon}</div><div className="dsc-label">{s.label}</div><div className="dsc-value">{s.v}</div><div className="dsc-sub">{s.sub}</div>
          </div>
        ))}
      </div>
      <div className="db-stats-grid-4" style={{marginBottom:24}}>
        {[{icon:'⭐',label:'Avg Platform Rating',v:S.avgRating},{icon:'🎫',label:'Open Tickets',v:S.openTickets},{icon:'✅',label:'Resolved Today',v:S.resolvedToday},{icon:'📊',label:'Booking Conversion',v:'68%'}].map(s=>(<div key={s.label} className="db-stat-card"><div className="dsc-icon">{s.icon}</div><div className="dsc-label">{s.label}</div><div className="dsc-value">{s.v}</div></div>))}
      </div>
      <div className="db-grid-2" style={{marginBottom:24}}>
        <div className="db-card">
          <div className="db-section-header"><span className="db-section-title">📈 Revenue (6 Months)</span></div>
          <div className="db-bar-chart" style={{height:130}}>
            {revenueByMonth.map(m=>{const max=Math.max(...revenueByMonth.map(x=>x.revenue));return(<div key={m.month} className="db-bar-col"><div className="db-bar-val" style={{fontSize:'.55rem'}}>{(m.revenue/100000).toFixed(1)}L</div><div style={{display:'flex',gap:2,alignItems:'flex-end',flex:1,width:'100%'}}><div className="db-bar" style={{flex:1,height:`${(m.revenue/max)*100}%`}}/><div className="db-bar secondary" style={{flex:1,height:`${(m.commission/max)*100}%`}}/></div><div className="db-bar-lbl">{m.month}</div></div>);})}</div>
          <div style={{display:'flex',gap:16,marginTop:10,paddingTop:10,borderTop:'1px solid #f3f4f6',fontSize:'.72rem',fontWeight:700}}><span style={{color:'#1DB954'}}>▬ Revenue</span><span style={{color:'#6366f1'}}>▬ Commission</span></div>
        </div>
        <div className="db-card">
          <div className="db-section-header"><span className="db-section-title">⏳ Pending Approvals</span><button className="db-section-action" onClick={()=>nav('/dashboard/admin/approvals')}>View all →</button></div>
          {pm.slice(0,3).map(m=>(
            <div key={m.id||m._id} className="session-row">
              <Av seed={m.avatarSeed} bg={m.avatarBg} init={m.initials}/>
              <div className="sr-info"><div className="sr-name">{m.name}</div><div className="sr-meta">{m.role} · {m.company} · {m.exp} yrs</div><div style={{fontSize:'.7rem',color:'#9ca3af'}}>{m.submittedDate}</div></div>
              <div style={{display:'flex',gap:6}}>
                <button className="btn btn-primary btn-sm" style={{padding:'4px 10px',fontSize:'.72rem'}} onClick={()=>onApprove&&onApprove(m._id||m.id)}>✓ Approve</button>
                <button className="btn btn-outline btn-sm" style={{padding:'4px 10px',fontSize:'.72rem',color:'#dc2626',borderColor:'#fca5a5'}} onClick={()=>onReject&&onReject(m._id||m.id)}>✗ Reject</button>
              </div>
            </div>
          ))}
          {pm.length === 0 && <div style={{fontSize:'.84rem',color:'#9ca3af',padding:'12px 0'}}>No pending applications right now.</div>}
        </div>
      </div>
      <div className="db-card">
        <div className="db-section-header"><span className="db-section-title">📋 Recent Bookings</span><button className="db-section-action" onClick={()=>nav('/dashboard/admin/bookings')}>View all →</button></div>
        <div className="db-table-wrap"><table className="db-table"><thead><tr><th>ID</th><th>Mentee</th><th>Mentor</th><th>Type</th><th>Date</th><th>Amount</th><th>Commission</th><th>Status</th></tr></thead><tbody>
          {recentBookings.map(b=>(<tr key={b.id}><td style={{fontWeight:700,color:'#2563eb',fontSize:'.75rem'}}>{b.id}</td><td>{b.mentee}</td><td>{b.mentor}</td><td style={{fontSize:'.8rem'}}>{b.type}</td><td>{b.date}</td><td style={{fontWeight:800}}>₹{b.amount.toLocaleString()}</td><td style={{color:'#16a34a',fontWeight:700}}>₹{b.commission.toLocaleString()}</td><td><span className={`st-badge st-${b.status}`}>{b.status}</span></td></tr>))}
        </tbody></table></div>
      </div>
    </div>
  );
}

function Users({ users: _users }) {
  const isMock = process.env.REACT_APP_MOCK !== 'false';
  const [allUsers,      setAllUsers]     = useState(isMock ? D.allUsers : (_users||[]));
  const [loading,       setLoading]      = useState(!isMock && !_users?.length);
  const [search,        setSearch]       = useState('');
  const [roleFilter,    setRoleFilter]   = useState('all');
  const [statusFilter,  setStatusFilter] = useState('all');
  const [actionMsg,     setActionMsg]    = useState('');

  useEffect(()=>{
    if(isMock||(_users&&_users.length)) return;
    if(!sessionStorage.getItem('db_user')){ setLoading(false); return; }
    apiClient.get('/admin/users',{params:{limit:100}}).then(res=>{
      const mapped=(res.users||[]).map(u=>({
        id:u._id, name:`${u.firstName} ${u.lastName}`, email:u.email,
        role:u.role, status:u.isApproved?'active':'pending',
        joined: new Date(u.createdAt).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}),
        sessions:0, rating:null,
        avatarBg:u.avatarBg||'1DB954', init:`${u.firstName?.[0]||''}${u.lastName?.[0]||''}`.toUpperCase(),
      }));
      setAllUsers(mapped);
    }).catch(()=>{}).finally(()=>setLoading(false));
  },[]);// eslint-disable-line

  const handleAction = async (uid, action) => {
    if(isMock){ setActionMsg(`[Mock] ${action} applied`); return; }
    try{
      await apiClient.put(`/admin/users/${uid}/${action}`);
      setAllUsers(p=>p.map(u=>u.id===uid?{...u,status:action==='approve'?'active':action==='ban'?'suspended':u.status}:u));
      setActionMsg(`✅ User ${action}d`);
    }catch(e){ setActionMsg(`❌ ${e.message}`); }
    setTimeout(()=>setActionMsg(''),3000);
  };

  const filtered = allUsers.filter(u=>{
    const match=u.name.toLowerCase().includes(search.toLowerCase())||u.email.toLowerCase().includes(search.toLowerCase());
    return match&&(roleFilter==='all'||u.role===roleFilter)&&(statusFilter==='all'||u.status===statusFilter);
  });
  return (
    <div>
      <h1 className="db-page-title">User Management</h1><p className="db-page-sub">View and manage all mentors and mentees.</p>
      {actionMsg&&<div style={{padding:'10px 16px',background:'#f0fdf4',borderRadius:8,marginBottom:16,fontWeight:700,color:'#16a34a',fontSize:'.84rem'}}>{actionMsg}</div>}
      <div style={{display:'flex',gap:10,marginBottom:20,flexWrap:'wrap'}}>
        <input className="db-field" placeholder="🔍 Search by name or email..." style={{flex:1,minWidth:220}} value={search} onChange={e=>setSearch(e.target.value)}/>
        <select className="db-field" style={{width:140}} value={roleFilter} onChange={e=>setRoleFilter(e.target.value)}><option value="all">All Roles</option><option value="mentor">Mentors</option><option value="mentee">Mentees</option></select>
        <select className="db-field" style={{width:140}} value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}><option value="all">All Status</option><option value="active">Active</option><option value="pending">Pending</option><option value="suspended">Suspended</option></select>
      </div>
      <div className="db-card">
        {loading ? <div style={{padding:32,textAlign:'center',color:'#9ca3af'}}>Loading users…</div>
          : filtered.length===0 ? <div style={{padding:32,textAlign:'center',color:'#9ca3af'}}>No users found.</div>
          : <div className="db-table-wrap"><table className="db-table"><thead><tr><th>User</th><th>Role</th><th>Status</th><th>Joined</th><th>Actions</th></tr></thead><tbody>
          {filtered.map(u=>(<tr key={u.id}>
            <td><div style={{display:'flex',alignItems:'center',gap:10}}><Av seed={u.avatarSeed||''} bg={u.avatarBg||'1DB954'} init={u.init||'U'}/><div><div style={{fontWeight:800,fontSize:'.84rem'}}>{u.name}</div><div style={{fontSize:'.72rem',color:'#9ca3af'}}>{u.email}</div></div></div></td>
            <td><span className={`st-badge st-${u.role}`}>{u.role}</span></td>
            <td><span className={`st-badge st-${u.status==='active'?'completed':u.status==='pending'?'pending':'cancelled'}`}>{u.status}</span></td>
            <td style={{fontSize:'.8rem'}}>{u.joined}</td>
            <td><div style={{display:'flex',gap:6}}>
              {u.status!=='active'&&<button style={{fontSize:'.7rem',color:'#16a34a',background:'none',border:'none',cursor:'pointer',fontWeight:700}} onClick={()=>handleAction(u.id,'approve')}>Approve</button>}
              {u.status==='active'&&<button style={{fontSize:'.7rem',color:'#dc2626',background:'none',border:'none',cursor:'pointer',fontWeight:700}} onClick={()=>handleAction(u.id,'ban')}>Suspend</button>}
              <button style={{fontSize:'.7rem',color:'#6b7280',background:'none',border:'none',cursor:'pointer',fontWeight:700}}>Reset PW</button>
            </div></td>
          </tr>))}
        </tbody></table></div>}
      </div>
    </div>
  );
}

function Bookings({ bookings: _bookings, stats: _stats }) {
  const isMock = process.env.REACT_APP_MOCK !== 'false';
  const [allBookings, setAllBookings] = useState(isMock ? D.recentBookings : (_bookings||[]));
  const [loading,     setLoading]     = useState(!isMock && !_bookings?.length);
  const [search,      setSearch]      = useState('');
  const [statusFilter,setStatusFilter]= useState('all');
  const S = isMock ? D.platformStats : (_stats||{});

  useEffect(()=>{
    if(isMock||(_bookings&&_bookings.length)) return;
    if(!sessionStorage.getItem('db_user')){ setLoading(false); return; }
    apiClient.get('/admin/transactions',{params:{limit:100}}).then(res=>{
      const bks=(res.transactions||res.bookings||[]).map(b=>({
        id: b.bookingRef||b._id,
        mentee: b.menteeId ? `${b.menteeId.firstName||''} ${b.menteeId.lastName||''}`.trim() : 'Mentee',
        mentor: b.mentorId?.userId ? `${b.mentorId.userId.firstName||''} ${b.mentorId.userId.lastName||''}`.trim() : 'Mentor',
        type:   b.sessionType||'1:1 Session',
        date:   b.sessionDate ? new Date(b.sessionDate).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}) : '-',
        amount: b.priceINR||b.amount||0, commission: b.platformFeeINR||b.commission||0,
        status: b.status,
      }));
      setAllBookings(bks);
    }).catch(()=>{}).finally(()=>setLoading(false));
  },[]);// eslint-disable-line

  const filtered = allBookings.filter(b=>{
    const q=search.toLowerCase();
    return (statusFilter==='all'||b.status===statusFilter)&&((b.mentee||'').toLowerCase().includes(q)||(b.mentor||'').toLowerCase().includes(q));
  });
  return (
    <div>
      <h1 className="db-page-title">Booking Management</h1><p className="db-page-sub">View and manage all platform bookings.</p>
      <div style={{display:'flex',gap:10,marginBottom:20,flexWrap:'wrap'}}>
        <input className="db-field" placeholder="🔍 Search mentee or mentor..." style={{flex:1,minWidth:200}} value={search} onChange={e=>setSearch(e.target.value)}/>
        <select className="db-field" style={{width:160}} value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}><option value="all">All Status</option><option value="confirmed">Confirmed</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option></select>
      </div>
      <div className="db-stats-grid-3" style={{marginBottom:20}}>
        {[{l:'Total Bookings',v:(S.totalBookings||allBookings.length).toLocaleString()},{l:'Active Now',v:S.activeBookings||allBookings.filter(b=>b.status==='confirmed').length},{l:'Total Revenue',v:`₹${((S.totalRevenue||0)/100000).toFixed(1)}L`}].map(s=>(<div key={s.l} className="db-stat-card"><div className="dsc-label">{s.l}</div><div className="dsc-value" style={{fontSize:'1.4rem'}}>{s.v}</div></div>))}
      </div>
      <div className="db-card">
        {loading ? <div style={{padding:32,textAlign:'center',color:'#9ca3af'}}>Loading bookings…</div>
          : filtered.length===0 ? <div style={{padding:32,textAlign:'center',color:'#9ca3af'}}>No bookings found.</div>
          : <div className="db-table-wrap"><table className="db-table"><thead><tr><th>Booking ID</th><th>Mentee</th><th>Mentor</th><th>Type</th><th>Date</th><th>Amount</th><th>Commission</th><th>Status</th></tr></thead><tbody>
            {filtered.map(b=>(<tr key={b.id}><td style={{fontWeight:700,color:'#2563eb',fontSize:'.75rem'}}>{b.id}</td><td>{b.mentee}</td><td>{b.mentor}</td><td style={{fontSize:'.8rem'}}>{b.type}</td><td>{b.date}</td><td style={{fontWeight:800}}>₹{(b.amount||0).toLocaleString()}</td><td style={{color:'#16a34a',fontWeight:700}}>₹{(b.commission||0).toLocaleString()}</td><td><span className={`st-badge st-${b.status}`}>{b.status}</span></td></tr>))}
          </tbody></table></div>}
      </div>
    </div>
  );
}

function Finance({ stats: _stats }) {
  const isMock = process.env.REACT_APP_MOCK !== 'false';
  const S = isMock ? D.platformStats : (_stats || {});
  const [payouts, setPayouts] = useState([]);
  const [loadingP, setLoadingP] = useState(!isMock);

  useEffect(()=>{
    if(isMock) return;
    if(!sessionStorage.getItem('db_user')){ setLoadingP(false); return; }
    apiClient.get('/admin/transactions',{params:{type:'payout',limit:20}}).then(res=>{
      setPayouts(res.transactions||res.payouts||[]);
    }).catch(()=>{}).finally(()=>setLoadingP(false));
  },[]);// eslint-disable-line

  const totalRev   = S.totalRevenue||0;
  const commission = S.totalCommission||0;
  const monthRev   = S.monthRevenue||0;
  const avgPerBook = S.totalBookings>0 ? Math.round(totalRev/S.totalBookings) : 0;
  const pendingPay = S.pendingPayouts||0;

  return (
    <div>
      <h1 className="db-page-title">Financial Management</h1><p className="db-page-sub">Revenue, commissions, and payouts.</p>
      <div className="db-stats-grid-4" style={{marginBottom:24}}>
        {[{icon:'💰',label:'Total Revenue',v:`₹${(totalRev/100000).toFixed(1)}L`},{icon:'📊',label:'Commission Earned',v:`₹${(commission/100000).toFixed(1)}L`},{icon:'📅',label:'Monthly Revenue',v:`₹${(monthRev/1000).toFixed(0)}K`},{icon:'💳',label:'Avg per Booking',v:`₹${avgPerBook.toLocaleString()}`}].map(s=>(<div key={s.label} className="db-stat-card"><div className="dsc-icon">{s.icon}</div><div className="dsc-label">{s.label}</div><div className="dsc-value" style={{fontSize:'1.3rem'}}>{s.v}</div></div>))}
      </div>
      <div className="db-grid-2">
        <div className="db-card"><div className="db-section-title" style={{marginBottom:14}}>💳 Pending Payouts</div>
          <div style={{padding:'20px',background:'#fffbeb',borderRadius:12,marginBottom:14}}>
            <div style={{fontSize:'.65rem',fontWeight:800,color:'#b45309',textTransform:'uppercase'}}>Total Pending</div>
            <div style={{fontSize:'1.8rem',fontWeight:800,color:'#b45309',marginTop:4}}>₹{pendingPay.toLocaleString()}</div>
          </div>
          {loadingP ? <div style={{color:'#9ca3af',fontSize:'.84rem'}}>Loading payouts…</div>
          : payouts.length>0 ? <div style={{fontSize:'.84rem',color:'#374151',marginBottom:10}}>{payouts.length} payout request(s) pending</div>
          : <div style={{fontSize:'.84rem',color:'#9ca3af'}}>No pending payouts</div>}
          <div style={{display:'flex',gap:8,marginTop:12}}><button className="btn btn-primary btn-sm" style={{flex:1,justifyContent:'center'}} onClick={()=>apiClient.put('/admin/payouts/process-all').catch(()=>{})} >Process All</button><button className="btn btn-outline btn-sm" style={{flex:1,justifyContent:'center'}}>Export</button></div>
        </div>
        <div className="db-card"><div className="db-section-title" style={{marginBottom:14}}>📊 Revenue Summary</div>
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            {[['Platform Revenue',`₹${(totalRev/100000).toFixed(2)}L`],['Commission (15%)',`₹${(commission/100000).toFixed(2)}L`],['Mentor Earnings',`₹${((totalRev-commission)/100000).toFixed(2)}L`],['This Month',`₹${(monthRev/1000).toFixed(0)}K`]].map(([l,v])=>(
              <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid #f3f4f6'}}><span style={{fontSize:'.84rem',color:'#374151',fontWeight:600}}>{l}</span><span style={{fontWeight:800}}>{v}</span></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Approvals({ initialPending = [] }) {
  const isMock = process.env.REACT_APP_MOCK !== 'false';
  const [pending,  setPending]  = useState(initialPending);
  const [reviewed, setReviewed] = useState([]);
  const [loading,  setLoading]  = useState(!isMock);
  const [fetchErr, setFetchErr] = useState('');
  const [actionMsg,setActionMsg]= useState('');
  const [tab,      setTab]      = useState('pending');

  // ── Pending tab filters + sort ──────────────────────────────
  const [pendingSearch,      setPendingSearch]      = useState('');
  const [pendingExpFilter,   setPendingExpFilter]   = useState('all');
  const [pendingSkillFilter, setPendingSkillFilter] = useState('');
  const [pendingSort,        setPendingSort]        = useState('date_desc'); // date_desc | date_asc | name_az | name_za | exp_high | exp_low

  // ── Approved tab filters + sort ──────────────────────────────
  const [approvedSearch,        setApprovedSearch]        = useState('');
  const [approvedStatusFilter,  setApprovedStatusFilter]  = useState('all'); // all | active | suspended
  const [approvedSort,          setApprovedSort]          = useState('date_desc'); // date_desc | date_asc | name_az | name_za

  // ── helpers: read/write persisted decisions (mock only) ──────
  const _readActed   = () => { try { return JSON.parse(localStorage.getItem('db_approvals_acted') || '{}'); } catch { return {}; } };
  const _writeActed  = (obj) => { try { localStorage.setItem('db_approvals_acted', JSON.stringify(obj)); } catch {} };
  const _readHistory = () => { try { return JSON.parse(localStorage.getItem('db_approvals_history') || '[]'); } catch { return []; } };
  const _writeHistory= (arr) => { try { localStorage.setItem('db_approvals_history', JSON.stringify(arr)); } catch {} };

  useEffect(() => {
    if (isMock) {
      const actedOn = _readActed();

      // ── Static mock pending — exclude already-decided entries ──
      const base = D.pendingMentors
        .filter(m => !actedOn[m.id])
        .map(m => ({ ...m, _source: 'mock' }));

      // ── Real in-app applications (submitted via BecomeMentorModal) ──
      const realApps = [];
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (!key?.startsWith('db_mentor_app_')) continue;
          const app = JSON.parse(localStorage.getItem(key) || '{}');
          if (app?.status !== 'pending' || !app?.name) continue;
          realApps.push({
            id:            key.replace('db_mentor_app_', ''),
            name:          app.name,
            email:         app.email      || '',
            role:          app.headline   || 'Developer',
            company:       app.company    || '',
            experience:    app.experience || app.exp || 0,
            skills:        app.skills     || [],
            bio:           app.bio        || '',
            linkedin:      app.linkedin   || '',
            submittedDate: app.submittedAt
              ? new Date(app.submittedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
              : 'Just now',
            initials:  app.initials || (app.name?.split(' ').map(n => n[0]).join('') || 'U').toUpperCase(),
            avatarBg:  app.avatarBg || '1DB954',
            _source:   'real',
            _localKey: key,
          });
        }
      } catch {}

      setReviewed(_readHistory());
      setPending([...realApps, ...base]);
      setLoading(false);
      return;
    }

    // ── Live mode: fetch pending + approved from real API ────────
    if (!sessionStorage.getItem('db_user')) { setLoading(false); return; }

    Promise.all([
      apiClient.get('/admin/mentors', { params: { status: 'pending',  limit: 100 } }),
      apiClient.get('/admin/mentors', { params: { status: 'approved', limit: 100 } }),
    ])
      .then(([pendingRes, approvedRes]) => {
        console.log('[Approvals] pending:', pendingRes, 'approved:', approvedRes);

        const pendingList  = pendingRes.mentors  || [];
        const approvedList = approvedRes.mentors || [];

        console.log('[Approvals] raw pending count:', pendingList.length, '| approved count:', approvedList.length);

        const mappedPending  = normalizePendingMentors(pendingList);
        const mappedApproved = normalizePendingMentors(approvedList).map(m => ({ ...m, decision: 'approved' }));

        console.log('[Approvals] normalized pending:', mappedPending.length, '| approved:', mappedApproved.length);

        setPending(mappedPending);
        setReviewed(mappedApproved);
        setFetchErr('');
      })
      .catch((err) => {
        console.error('[Approvals] fetch error:', err);
        const msg = err?.message || err?.code || JSON.stringify(err) || 'Unknown error';
        setFetchErr(`Could not load approvals: ${msg}`);
        // Fall back to initialPending if API fails
        if (initialPending.length > 0) setPending(initialPending);
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const decide = async (item, action) => {
    const mid = item._id || item.id;

    if (isMock) {
      const entry = { ...item, decision: action, decidedAt: Date.now() };

      if (item._source === 'real' && item._localKey) {
        // Update the applicant's own record so they see the result on next login
        try {
          const stored = JSON.parse(localStorage.getItem(item._localKey) || '{}');
          localStorage.setItem(item._localKey, JSON.stringify({ ...stored, status: action, [`${action}At`]: Date.now() }));
        } catch {}
      } else {
        // Persist mock-entry decision so it never reappears after refresh
        const acted = _readActed();
        acted[mid] = action;
        _writeActed(acted);
      }

      // Append to persisted history so "Reviewed" tab survives refresh
      const history = _readHistory();
      _writeHistory([...history, entry]);

      setPending(prev => prev.filter(m => (m._id || m.id) !== mid));
      setReviewed(prev => [...prev, entry]);
      setActionMsg(`${action === 'approved' ? '✅ Approved' : '❌ Rejected'}: ${item.name || 'Mentor'}`);
      setTimeout(() => setActionMsg(''), 3000);
      return;
    }

    // ── Live mode: call real API, optimistically remove from pending list ──
    // Backend /admin/users/:id/approve expects the USER's _id (plain string, not Mentor doc _id)
    const rawUid = item.userId;
    const userIdForApi = (typeof rawUid === 'object' ? String(rawUid?._id || rawUid || '') : rawUid) || mid;
    setPending(prev => prev.filter(m => (m._id || m.id) !== mid)); // optimistic
    try {
      await apiClient.put(`/admin/users/${userIdForApi}/${action === 'approved' ? 'approve' : 'reject'}`);
      setReviewed(prev => [...prev, { ...item, decision: action }]);
      setActionMsg(`${action === 'approved' ? '✅ Approved' : '❌ Rejected'}: ${item.name || mid}`);
      setTimeout(() => setActionMsg(''), 3000);
    } catch (e) {
      // Roll back optimistic removal if API fails
      setPending(prev => [...prev, item]);
      setActionMsg(`❌ Failed: ${e.message}`);
    }
  };

  // ── Suspend / Unsuspend approved mentors ──────────────────────
  const suspend = async (item) => {
    const mid   = item._id || item.id;
    const isActive = item.isActive !== false; // true by default if not set
    const action = isActive ? 'suspend' : 'unsuspend';

    if (isMock) {
      setReviewed(prev => prev.map(m => (m._id || m.id) === mid ? { ...m, isActive: !isActive } : m));
      setActionMsg(`${isActive ? '⏸️ Suspended' : '▶️ Unsuspended'}: ${item.name || 'Mentor'}`);
      setTimeout(() => setActionMsg(''), 3000);
      return;
    }

    const rawUid = item.userId;
    const userIdForApi = (typeof rawUid === 'object' ? String(rawUid?._id || rawUid || '') : rawUid) || mid;
    // Optimistic update
    setReviewed(prev => prev.map(m => (m._id || m.id) === mid ? { ...m, isActive: !isActive } : m));
    try {
      await apiClient.put(`/admin/users/${userIdForApi}/${action}`);
      setActionMsg(`${isActive ? '⏸️ Suspended' : '▶️ Unsuspended'}: ${item.name || mid}`);
      setTimeout(() => setActionMsg(''), 3000);
    } catch (e) {
      // Roll back on failure
      setReviewed(prev => prev.map(m => (m._id || m.id) === mid ? { ...m, isActive: isActive } : m));
      setActionMsg(`❌ Failed: ${e.message}`);
      setTimeout(() => setActionMsg(''), 4000);
    }
  };

  const renderCard = (m, showActions = true) => {
    const u    = m.userId || m;
    const name = u.firstName ? `${u.firstName} ${u.lastName}` : (m.name || 'Mentor');
    const email= u.email || m.email || '';
    const init = m.initials || `${(u.firstName || name || 'M')[0]}${(u.lastName || '')[0] || ''}`.toUpperCase();
    const skills = m.skills || [];
    const mid  = m._id || m.id;
    const isNew = m._source === 'real';

    return (
      <div key={mid} className="db-card">
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap', padding: '4px 0' }}>
          <Av seed={u.avatarSeed || u.avatar || m.avatarSeed || ''} bg={u.avatarBg || m.avatarBg || '1DB954'} init={init} size={52} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
              <span style={{ fontWeight: 800, fontSize: '.9375rem' }}>{name}</span>
              {isNew && (
                <span style={{ fontSize: '.68rem', background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', padding: '2px 8px', borderRadius: 20, fontWeight: 700 }}>
                  🆕 New
                </span>
              )}
              <span style={{ fontSize: '.72rem', color: '#9ca3af' }}>{email}</span>
            </div>
            <div style={{ fontSize: '.84rem', color: '#374151', marginBottom: 6 }}>
              {m.role || u.role || '—'}{m.company ? ` · ${m.company}` : ''} · {m.experience || m.exp || 0} yrs experience
            </div>
            {skills.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                {skills.slice(0, 5).map(s => (
                  <span key={s} className="badge badge-gray" style={{ fontSize: '.7rem' }}>{s}</span>
                ))}
              </div>
            )}
            {m.bio && (
              <div style={{ fontSize: '.76rem', color: '#6b7280', fontStyle: 'italic', marginBottom: 4 }}>
                "{m.bio.length > 110 ? m.bio.slice(0, 110) + '…' : m.bio}"
              </div>
            )}
            <div style={{ fontSize: '.75rem', color: '#9ca3af' }}>
              Applied: {m.submittedDate || (m.createdAt ? new Date(m.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—')}
            </div>
          </div>

          {showActions ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
              <button className="btn btn-primary btn-sm" onClick={() => decide(m, 'approved')}>✓ Approve</button>
              <button className="btn btn-outline btn-sm" style={{ color: '#dc2626', borderColor: '#fca5a5' }} onClick={() => decide(m, 'rejected')}>✗ Reject</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end', flexShrink: 0 }}>
              {/* Status badge */}
              <span className={`st-badge ${m.decision === 'approved' ? 'st-completed' : 'st-cancelled'}`}>
                {m.decision === 'approved' ? '✓ Approved' : '✗ Rejected'}
              </span>
              {/* Active / Suspended badge */}
              {m.decision === 'approved' && (
                <span style={{
                  fontSize: '.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                  background: m.isActive !== false ? '#f0fdf4' : '#fff7ed',
                  color:      m.isActive !== false ? '#16a34a' : '#ea580c',
                  border:     `1px solid ${m.isActive !== false ? '#bbf7d0' : '#fed7aa'}`,
                }}>
                  {m.isActive !== false ? '● Active' : '⏸ Suspended'}
                </span>
              )}
              {/* Suspend / Unsuspend button (approved mentors only) */}
              {m.decision === 'approved' && (
                <button
                  className="btn btn-outline btn-sm"
                  style={{
                    fontSize: '.72rem',
                    color:       m.isActive !== false ? '#ea580c' : '#16a34a',
                    borderColor: m.isActive !== false ? '#fed7aa' : '#bbf7d0',
                  }}
                  onClick={() => suspend(m)}
                >
                  {m.isActive !== false ? '⏸ Suspend' : '▶ Unsuspend'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <h1 className="db-page-title">✅ Mentor Approvals</h1>
      <p className="db-page-sub">Review new mentor applications. Approved mentors appear on the platform immediately.</p>

      {/* ── Data source indicator ─────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 16px', borderRadius: 10, marginBottom: 20,
        background: isMock ? '#fffbeb' : '#f0fdf4',
        border:     `1px solid ${isMock ? '#fde68a' : '#bbf7d0'}`,
      }}>
        <span style={{ fontSize: 16 }}>{isMock ? '🟡' : '🟢'}</span>
        <div style={{ flex: 1 }}>
          <span style={{ fontWeight: 700, fontSize: '.83rem', color: isMock ? '#92400e' : '#166534' }}>
            {isMock ? 'Mock Mode — browser storage only' : 'Live Mode — connected to database'}
          </span>
          <span style={{ fontSize: '.78rem', color: '#6b7280', marginLeft: 8 }}>
            {isMock
              ? 'Data is NOT from a real database. Decisions are saved to your browser and persist across refreshes.'
              : 'Data is fetched from the real backend. Approve/Reject actions update the database directly.'}
          </span>
        </div>
        {isMock && (
          <button
            onClick={() => {
              if (window.confirm('Clear all stored decisions? All mock entries will reappear as pending.')) {
                localStorage.removeItem('db_approvals_acted');
                localStorage.removeItem('db_approvals_history');
                window.location.reload();
              }
            }}
            style={{ fontSize: '.72rem', color: '#b45309', background: 'none', border: '1px solid #fde68a', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600 }}
          >
            Reset
          </button>
        )}
      </div>

      {actionMsg && (
        <div style={{ padding: '10px 16px', background: '#f0fdf4', borderRadius: 8, marginBottom: 16, fontWeight: 700, color: '#16a34a', fontSize: '.84rem' }}>
          {actionMsg}
        </div>
      )}

      {/* Stats */}
      <div className="db-stats-grid-4" style={{ marginBottom: 24 }}>
        {[
          { icon: '⏳', label: 'Pending Review',  v: pending.length },
          { icon: '✅', label: 'Total Approved',  v: reviewed.filter(r => r.decision === 'approved').length },
          { icon: '● Active',  label: 'Active Mentors',    v: reviewed.filter(r => r.decision === 'approved' && r.isActive !== false).length },
          { icon: '⏸',  label: 'Suspended',               v: reviewed.filter(r => r.decision === 'approved' && r.isActive === false).length },
        ].map(s => (
          <div key={s.label} className="db-stat-card">
            <div className="dsc-icon">{s.icon}</div>
            <div className="dsc-label">{s.label}</div>
            <div className="dsc-value">{s.v}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="db-tabs" style={{ marginBottom: 20 }}>
        <button className={`db-tab${tab === 'pending' ? ' active' : ''}`} onClick={() => setTab('pending')}>
          ⏳ Pending {pending.length > 0 ? `(${pending.length})` : ''}
        </button>
        <button className={`db-tab${tab === 'reviewed' ? ' active' : ''}`} onClick={() => setTab('reviewed')}>
          ✅ Approved {reviewed.length > 0 ? `(${reviewed.length})` : ''}
        </button>
      </div>

      {fetchErr && (
        <div style={{ padding: '10px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, marginBottom: 16, fontSize: '.82rem', color: '#dc2626' }}>
          ⚠️ {fetchErr}
          <button onClick={() => { setFetchErr(''); window.location.reload(); }}
            style={{ marginLeft: 12, fontSize: '.78rem', color: '#dc2626', background: 'none', border: '1px solid #fca5a5', borderRadius: 5, padding: '2px 8px', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading applications…</div>
      ) : tab === 'pending' ? (
        <>
          {/* ── Pending tab: filter + sort bar ──────────────────── */}
          <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 16px', marginBottom: 18 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
              <input
                className="db-field"
                placeholder="🔍 Search name or email…"
                style={{ flex: 1, minWidth: 200, background: '#fff' }}
                value={pendingSearch}
                onChange={e => setPendingSearch(e.target.value)}
              />
              <select className="db-field" style={{ width: 155, background: '#fff' }} value={pendingExpFilter} onChange={e => setPendingExpFilter(e.target.value)}>
                <option value="all">All Experience</option>
                <option value="0-2">0 – 2 yrs</option>
                <option value="3-5">3 – 5 yrs</option>
                <option value="5+">5 + yrs</option>
              </select>
              <input
                className="db-field"
                placeholder="🏷 Skill…"
                style={{ width: 155, background: '#fff' }}
                value={pendingSkillFilter}
                onChange={e => setPendingSkillFilter(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '.73rem', fontWeight: 700, color: '#6b7280', whiteSpace: 'nowrap' }}>Sort by:</span>
              {[
                { v: 'date_desc', l: '📅 Newest first' },
                { v: 'date_asc',  l: '📅 Oldest first' },
                { v: 'name_az',   l: '🔤 Name A→Z' },
                { v: 'name_za',   l: '🔤 Name Z→A' },
                { v: 'exp_high',  l: '📈 Exp high→low' },
                { v: 'exp_low',   l: '📉 Exp low→high' },
              ].map(opt => (
                <button
                  key={opt.v}
                  onClick={() => setPendingSort(opt.v)}
                  style={{
                    padding: '4px 12px', borderRadius: 20, fontSize: '.7rem', fontWeight: 700, cursor: 'pointer', border: '1.5px solid',
                    background: pendingSort === opt.v ? '#1a1a2e' : '#fff',
                    color:      pendingSort === opt.v ? '#fff'     : '#6b7280',
                    borderColor:pendingSort === opt.v ? '#1a1a2e'  : '#e5e7eb',
                  }}
                >{opt.l}</button>
              ))}
              {(pendingSearch || pendingExpFilter !== 'all' || pendingSkillFilter || pendingSort !== 'date_desc') && (
                <button
                  style={{ marginLeft: 'auto', padding: '4px 12px', borderRadius: 20, border: '1.5px solid #fca5a5', background: '#fff', fontSize: '.7rem', fontWeight: 700, color: '#dc2626', cursor: 'pointer' }}
                  onClick={() => { setPendingSearch(''); setPendingExpFilter('all'); setPendingSkillFilter(''); setPendingSort('date_desc'); }}
                >✕ Reset all</button>
              )}
            </div>
          </div>

          {(() => {
            const q  = pendingSearch.toLowerCase();
            const sk = pendingSkillFilter.toLowerCase();

            // 1. Filter
            const filtered = pending.filter(m => {
              const matchText = !q || (m.name||'').toLowerCase().includes(q) || (m.email||'').toLowerCase().includes(q);
              const exp = m.experience || m.exp || 0;
              const matchExp = pendingExpFilter === 'all'
                || (pendingExpFilter === '0-2' && exp <= 2)
                || (pendingExpFilter === '3-5' && exp >= 3 && exp <= 5)
                || (pendingExpFilter === '5+'  && exp > 5);
              const matchSkill = !sk || (m.skills||[]).some(s => s.toLowerCase().includes(sk));
              return matchText && matchExp && matchSkill;
            });

            // 2. Sort
            const _ts = m => m.submittedAt || (m.createdAt ? new Date(m.createdAt).getTime() : 0) || 0;
            const sorted = [...filtered].sort((a, b) => {
              if (pendingSort === 'date_desc') return _ts(b) - _ts(a);
              if (pendingSort === 'date_asc')  return _ts(a) - _ts(b);
              if (pendingSort === 'name_az')   return (a.name||'').localeCompare(b.name||'');
              if (pendingSort === 'name_za')   return (b.name||'').localeCompare(a.name||'');
              if (pendingSort === 'exp_high')  return (b.experience||b.exp||0) - (a.experience||a.exp||0);
              if (pendingSort === 'exp_low')   return (a.experience||a.exp||0) - (b.experience||b.exp||0);
              return 0;
            });

            if (sorted.length === 0) {
              return pending.length === 0 ? (
                <div className="db-card" style={{ padding: '40px 24px', textAlign: 'center' }}>
                  <div style={{ fontSize: '2.8rem', marginBottom: 12 }}>🎉</div>
                  <div style={{ fontWeight: 800, fontSize: '.95rem', color: '#111827', marginBottom: 4 }}>All caught up!</div>
                  <div style={{ fontSize: '.82rem', color: '#9ca3af' }}>No pending mentor applications right now.</div>
                </div>
              ) : (
                <div className="db-card" style={{ padding: '32px 24px', textAlign: 'center', color: '#9ca3af', fontSize: '.88rem' }}>
                  No results match your filters.
                </div>
              );
            }

            return (
              <>
                <div style={{ fontSize: '.75rem', color: '#9ca3af', fontWeight: 600, marginBottom: 10 }}>
                  Showing {sorted.length} of {pending.length} application{pending.length !== 1 ? 's' : ''}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {sorted.map(m => renderCard(m, true))}
                </div>
              </>
            );
          })()}
        </>
      ) : (
        <>
          {/* ── Approved tab: filter + sort bar ─────────────────── */}
          <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 16px', marginBottom: 18 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
              <input
                className="db-field"
                placeholder="🔍 Search name or email…"
                style={{ flex: 1, minWidth: 200, background: '#fff' }}
                value={approvedSearch}
                onChange={e => setApprovedSearch(e.target.value)}
              />
              <select className="db-field" style={{ width: 165, background: '#fff' }} value={approvedStatusFilter} onChange={e => setApprovedStatusFilter(e.target.value)}>
                <option value="all">All Status</option>
                <option value="active">Active only</option>
                <option value="suspended">Suspended only</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '.73rem', fontWeight: 700, color: '#6b7280', whiteSpace: 'nowrap' }}>Sort by:</span>
              {[
                { v: 'date_desc', l: '📅 Approved newest' },
                { v: 'date_asc',  l: '📅 Approved oldest' },
                { v: 'name_az',   l: '🔤 Name A→Z' },
                { v: 'name_za',   l: '🔤 Name Z→A' },
              ].map(opt => (
                <button
                  key={opt.v}
                  onClick={() => setApprovedSort(opt.v)}
                  style={{
                    padding: '4px 12px', borderRadius: 20, fontSize: '.7rem', fontWeight: 700, cursor: 'pointer', border: '1.5px solid',
                    background: approvedSort === opt.v ? '#1a1a2e' : '#fff',
                    color:      approvedSort === opt.v ? '#fff'     : '#6b7280',
                    borderColor:approvedSort === opt.v ? '#1a1a2e'  : '#e5e7eb',
                  }}
                >{opt.l}</button>
              ))}
              {(approvedSearch || approvedStatusFilter !== 'all' || approvedSort !== 'date_desc') && (
                <button
                  style={{ marginLeft: 'auto', padding: '4px 12px', borderRadius: 20, border: '1.5px solid #fca5a5', background: '#fff', fontSize: '.7rem', fontWeight: 700, color: '#dc2626', cursor: 'pointer' }}
                  onClick={() => { setApprovedSearch(''); setApprovedStatusFilter('all'); setApprovedSort('date_desc'); }}
                >✕ Reset all</button>
              )}
            </div>
          </div>

          {(() => {
            const q = approvedSearch.toLowerCase();

            // 1. Filter
            const filtered = reviewed.filter(m => {
              const matchText = !q || (m.name||'').toLowerCase().includes(q) || (m.email||'').toLowerCase().includes(q);
              const isActive  = m.isActive !== false;
              const matchStatus = approvedStatusFilter === 'all'
                || (approvedStatusFilter === 'active'    && isActive)
                || (approvedStatusFilter === 'suspended' && !isActive);
              return matchText && matchStatus;
            });

            // 2. Sort
            const _ts = m => m.decidedAt || m.approvedAt || (m.createdAt ? new Date(m.createdAt).getTime() : 0) || 0;
            const sorted = [...filtered].sort((a, b) => {
              if (approvedSort === 'date_desc') return _ts(b) - _ts(a);
              if (approvedSort === 'date_asc')  return _ts(a) - _ts(b);
              if (approvedSort === 'name_az')   return (a.name||'').localeCompare(b.name||'');
              if (approvedSort === 'name_za')   return (b.name||'').localeCompare(a.name||'');
              return 0;
            });

            if (sorted.length === 0) {
              return reviewed.length === 0 ? (
                <div className="db-card" style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: '.88rem' }}>
                  No approved mentors yet.
                </div>
              ) : (
                <div className="db-card" style={{ padding: '32px 24px', textAlign: 'center', color: '#9ca3af', fontSize: '.88rem' }}>
                  No results match your filters.
                </div>
              );
            }

            return (
              <>
                <div style={{ fontSize: '.75rem', color: '#9ca3af', fontWeight: 600, marginBottom: 10 }}>
                  Showing {sorted.length} of {reviewed.length} mentor{reviewed.length !== 1 ? 's' : ''}
                  {reviewed.filter(r => r.isActive === false).length > 0 && (
                    <span style={{ marginLeft: 10, color: '#ea580c' }}>
                      · {reviewed.filter(r => r.isActive === false).length} suspended
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {sorted.map(m => renderCard(m, false))}
                </div>
              </>
            );
          })()}
        </>
      )}
    </div>
  );
}

function SupportAdmin() {
  const [search,setSearch]=useState('');
  const [statusFilter,setStatusFilter]=useState('all');
  const isMockS = process.env.REACT_APP_MOCK !== 'false';
  const tickets = isMockS ? (D.tickets || []) : [];
  const filtered = tickets.filter(t=>(statusFilter==='all'||t.status===statusFilter)&&((t.user||'').toLowerCase().includes(search.toLowerCase())||(t.subject||'').toLowerCase().includes(search.toLowerCase())));
  const statusColor={open:'st-open',in_progress:'st-in_progress',resolved:'st-resolved',closed:'st-closed'};
  const prColor={high:'pr-high',medium:'pr-medium',low:'pr-low'};
  return (
    <div>
      <h1 className="db-page-title">Support Management</h1><p className="db-page-sub">Manage and respond to user support tickets.</p>
      <div style={{display:'flex',gap:10,marginBottom:20,flexWrap:'wrap'}}>
        <input className="db-field" placeholder="🔍 Search tickets..." style={{flex:1,minWidth:200}} value={search} onChange={e=>setSearch(e.target.value)}/>
        <select className="db-field" style={{width:160}} value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}><option value="all">All Status</option><option value="open">Open</option><option value="in_progress">In Progress</option><option value="resolved">Resolved</option><option value="closed">Closed</option></select>
      </div>
      <div className="db-stats-grid-4" style={{marginBottom:20}}>
        {[{l:'Open',v:tickets.filter(t=>t.status==='open').length,c:'#dc2626'},{l:'In Progress',v:tickets.filter(t=>t.status==='in_progress').length,c:'#b45309'},{l:'Resolved',v:tickets.filter(t=>t.status==='resolved').length,c:'#16a34a'},{l:'Closed',v:tickets.filter(t=>t.status==='closed').length,c:'#6b7280'}].map(s=>(<div key={s.l} className="db-stat-card"><div className="dsc-label">{s.l}</div><div className="dsc-value" style={{color:s.c}}>{s.v}</div></div>))}
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:12}}>
        {filtered.map(t=>(
          <div key={t.id} className="db-card">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:12}}>
              <div style={{flex:1}}>
                <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',marginBottom:6}}>
                  <span style={{fontWeight:700,fontSize:'.72rem',color:'#2563eb'}}>#{t.id}</span>
                  <span className={`st-badge st-${t.role}`}>{t.role}</span>
                  <span className={`st-badge ${prColor[t.priority]}`}>{t.priority}</span>
                  <span className={`st-badge ${statusColor[t.status]}`}>{t.status.replace('_',' ')}</span>
                </div>
                <div style={{fontWeight:800,fontSize:'.875rem',color:'#1a1a2e',marginBottom:4}}>{t.subject}</div>
                <div style={{fontSize:'.75rem',color:'#9ca3af'}}>From: {t.user} · {t.created} · Assigned: {t.assignedTo}</div>
              </div>
              <div style={{display:'flex',gap:8,alignItems:'center',flexShrink:0}}>
                <select style={{padding:'6px 10px',borderRadius:8,border:'1px solid #e5e7eb',fontSize:'.75rem',fontFamily:'inherit',cursor:'pointer'}} defaultValue={t.status}>
                  <option value="open">Open</option><option value="in_progress">In Progress</option><option value="resolved">Resolved</option><option value="closed">Closed</option>
                </select>
                <button className="btn btn-primary btn-sm">Respond</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── FEATURE FLAGS ─────────────────────────────────────────── */
function FeatureFlags() {
  const { flags, toggleFlag, resetAll, definitions } = useFeatureFlags();
  const categories = [...new Set(definitions.map(d => d.category))];

  // Category colour accents
  const catColors = {
    Core: '#16a34a', Community: '#2563eb', Trust: '#f59e0b',
    Certifications: '#7c3aed', UI: '#0891b2', Marketing: '#ec4899',
    Advanced: '#ef4444', Support: '#6b7280',
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 className="db-page-title">🚩 Feature Flags</h1>
          <p className="db-page-sub">Enable or disable platform features dynamically. Changes take effect immediately — no redeploy needed.</p>
        </div>
        <button onClick={resetAll} style={{ padding:'8px 16px', borderRadius:9, border:'1.5px solid #e5e7eb', background:'#fff', fontFamily:'inherit', fontSize:'.8rem', fontWeight:700, color:'#6b7280', cursor:'pointer' }}>
          ↺ Reset to Defaults
        </button>
      </div>

      {/* Summary bar */}
      <div style={{ display:'flex', gap:10, marginBottom:24, flexWrap:'wrap' }}>
        <div style={{ padding:'8px 16px', borderRadius:10, background:'#f0fdf4', border:'1px solid #bbf7d0', fontSize:'.78rem', fontWeight:700, color:'#16a34a' }}>
          ✅ {definitions.filter(d => flags[d.key]).length} Enabled
        </div>
        <div style={{ padding:'8px 16px', borderRadius:10, background:'#fef2f2', border:'1px solid #fecaca', fontSize:'.78rem', fontWeight:700, color:'#ef4444' }}>
          ✗ {definitions.filter(d => !flags[d.key]).length} Disabled
        </div>
        <div style={{ padding:'8px 16px', borderRadius:10, background:'#f8fafc', border:'1px solid #e2e8f0', fontSize:'.78rem', fontWeight:600, color:'#64748b' }}>
          {definitions.length} Total Features
        </div>
      </div>

      {categories.map(cat => {
        const catDefs = definitions.filter(d => d.category === cat);
        const accent  = catColors[cat] || '#6b7280';
        return (
          <div key={cat} className="db-card" style={{ marginBottom:18, overflow:'hidden' }}>
            <div style={{ padding:'12px 18px', borderBottom:'1px solid #f3f4f6', display:'flex', alignItems:'center', gap:10, borderLeft:`3px solid ${accent}` }}>
              <div style={{ fontWeight:800, fontSize:'.9rem', color:'#1a1a2e' }}>{cat}</div>
              <div style={{ fontSize:'.7rem', color:'#9ca3af', fontWeight:600 }}>{catDefs.length} feature{catDefs.length !== 1 ? 's' : ''}</div>
              <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
                {catDefs.filter(f => flags[f.key]).length > 0 && (
                  <span style={{ background:'#f0fdf4', color:'#16a34a', padding:'2px 8px', borderRadius:100, fontSize:'.65rem', fontWeight:800 }}>
                    {catDefs.filter(f => flags[f.key]).length} on
                  </span>
                )}
              </div>
            </div>
            <div style={{ padding:'8px 0' }}>
              {catDefs.map(f => (
                <div key={f.key} className="ff-row" style={{ borderLeft: flags[f.key] ? `2px solid ${accent}20` : 'none' }}>
                  <div className="ff-icon">{f.icon}</div>
                  <div className="ff-info">
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div className="ff-label">{f.label}</div>
                      {/* Mobile release special badge */}
                      {f.key === 'mobileRelease' && (
                        <span style={{ background:'linear-gradient(90deg,#ec4899,#8b5cf6)', color:'#fff', padding:'1px 8px', borderRadius:100, fontSize:'.6rem', fontWeight:800 }}>
                          📱 NEW
                        </span>
                      )}
                      {!f.defaultEnabled && (
                        <span style={{ background:'#fef3c7', color:'#92400e', padding:'1px 8px', borderRadius:100, fontSize:'.6rem', fontWeight:700 }}>
                          Off by default
                        </span>
                      )}
                    </div>
                    <div className="ff-desc">{f.description}</div>
                    {f.affectsRoutes.length > 0 && (
                      <div className="ff-routes">Affects routes: {f.affectsRoutes.join(', ')}</div>
                    )}
                    {/* Mobile release: show what will appear */}
                    {f.key === 'mobileRelease' && flags[f.key] && (
                      <div style={{ marginTop:6, padding:'8px 10px', background:'linear-gradient(90deg,rgba(236,72,153,.08),rgba(139,92,246,.08))', borderRadius:8, fontSize:'.72rem', color:'#7c3aed', fontWeight:600, border:'1px solid rgba(139,92,246,.2)' }}>
                        ✅ App Store + Google Play download section is <strong>visible</strong> on the Home page
                      </div>
                    )}
                    {f.key === 'mobileRelease' && !flags[f.key] && (
                      <div style={{ marginTop:6, padding:'8px 10px', background:'#f8fafc', borderRadius:8, fontSize:'.72rem', color:'#64748b', border:'1px solid #e2e8f0' }}>
                        🔒 App Store + Google Play section is <strong>hidden</strong> from the Home page
                      </div>
                    )}
                  </div>
                  <div className="ff-right">
                    <span className={`ff-status ${flags[f.key] ? 'on' : 'off'}`}>
                      {flags[f.key] ? '✓ Enabled' : '✗ Disabled'}
                    </span>
                    <button
                      className={`ff-toggle ${flags[f.key] ? 'on' : ''}`}
                      onClick={() => toggleFlag(f.key)}
                      title={flags[f.key] ? 'Disable feature' : 'Enable feature'}
                    >
                      <div className="ff-knob" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── CERTIFICATION MANAGEMENT ───────────────────────────────── */
function CertificationMgmt() {
  const [courses, setCourses] = useState(COURSES);
  const [enrollments] = useState(ALL_ENROLLMENTS);
  const [tab, setTab] = useState('courses');
  const [showAddForm, setShowAddForm] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [editCourse, setEditCourse] = useState(null);

  const deleteCourse = (id) => {
    if (window.confirm('Remove this course? This cannot be undone.')) {
      setCourses(prev => prev.filter(c => c.id !== id));
    }
  };

  const statusColor = { not_started:'#9ca3af', in_progress:'#2563eb', under_review:'#f59e0b', approved:'#16a34a', rejected:'#ef4444', completed:'#16a34a' };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 className="db-page-title">🏆 Certification Management</h1>
          <p className="db-page-sub">Add, edit, assign mentors, and manage courses and student progress.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAddForm(true)} style={{ fontSize:'.84rem', padding:'9px 18px' }}>
          + Add Course
        </button>
      </div>

      {/* Stats */}
      <div className="db-stats-grid-4" style={{ marginBottom:24 }}>
        {[
          { icon:'📚', label:'Total Courses', v: courses.length },
          { icon:'👩‍🎓', label:'Total Enrollments', v: ALL_ENROLLMENTS.length },
          { icon:'⏳', label:'Under Review', v: ALL_ENROLLMENTS.filter(e=>e.status==='under_review').length },
          { icon:'🎓', label:'Completed', v: ALL_ENROLLMENTS.filter(e=>e.status==='completed').length },
        ].map(s => (
          <div key={s.label} className="db-stat-card">
            <div className="dsc-icon">{s.icon}</div>
            <div className="dsc-label">{s.label}</div>
            <div className="dsc-value">{s.v}</div>
          </div>
        ))}
      </div>

      {/* Add Course Form */}
      {showAddForm && (
        <div className="db-card" style={{ marginBottom:20 }}>
          <div style={{ padding:'14px 18px', borderBottom:'1px solid #f3f4f6', fontWeight:800, fontSize:'.9rem' }}>
            ➕ Add New Course
          </div>
          <div style={{ padding:'18px' }}>
            <div className="db-form-grid">
              <div className="db-form-field"><label className="db-form-label">Course Name *</label><input className="db-form-input" placeholder="e.g. Full-Stack React Developer" /></div>
              <div className="db-form-field"><label className="db-form-label">Icon (emoji)</label><input className="db-form-input" placeholder="⚛️" style={{ width:80 }} /></div>
              <div className="db-form-field full"><label className="db-form-label">Description *</label><textarea className="db-form-input" rows={3} placeholder="What will students learn?" style={{ width:'100%', resize:'vertical' }} /></div>
              <div className="db-form-field"><label className="db-form-label">Level</label>
                <select className="db-form-input"><option>Beginner</option><option>Intermediate</option><option>Advanced</option></select>
              </div>
              <div className="db-form-field"><label className="db-form-label">Duration</label><input className="db-form-input" placeholder="8 weeks" /></div>
              <div className="db-form-field"><label className="db-form-label">Price (₹)</label><input className="db-form-input" type="number" placeholder="6999" /></div>
              <div className="db-form-field"><label className="db-form-label">Assign Mentor</label>
                <select className="db-form-input">
                  {courses.map(c => <option key={c.id}>{c.mentorName}</option>)}
                </select>
              </div>
              <div className="db-form-field full"><label className="db-form-label">Skills (comma-separated)</label><input className="db-form-input" placeholder="React, Node.js, MongoDB" /></div>
            </div>
            <div style={{ marginTop:14, display:'flex', gap:10 }}>
              <button className="btn btn-primary" style={{ fontSize:'.84rem', padding:'9px 18px' }} onClick={() => setShowAddForm(false)}>Save Course</button>
              <button onClick={() => setShowAddForm(false)} style={{ padding:'9px 18px', borderRadius:9, border:'1.5px solid #e5e7eb', background:'#fff', fontFamily:'inherit', fontSize:'.84rem', fontWeight:700, cursor:'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Tab */}
      <div className="db-card">
        <div style={{ padding:'12px 18px', borderBottom:'1px solid #f3f4f6', display:'flex', gap:8 }}>
          {['courses', 'enrollments'].map(t => (
            <button key={t} className={`btn btn-sm ${tab === t ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab(t)}>
              {t === 'courses' ? `📚 Courses (${courses.length})` : `👩‍🎓 Enrollments (${enrollments.length})`}
            </button>
          ))}
        </div>

        {tab === 'courses' && (
          <div>
            {courses.map(c => (
              <div key={c.id} className="cert-course-row">
                <div className="ccr-icon">{c.icon}</div>
                <div className="ccr-body">
                  <div className="ccr-name">{c.name}</div>
                  <div className="ccr-meta">
                    <span className={`st-badge st-${c.level.toLowerCase().split('–')[0].trim()}`}>{c.level}</span>
                    <span style={{ fontSize:'.72rem', color:'#6b7280' }}>🕐 {c.duration}</span>
                    <span style={{ fontSize:'.72rem', color:'#6b7280' }}>📚 {c.tasks.length} tasks</span>
                    <span style={{ fontSize:'.72rem', color:'#6b7280' }}>👩‍🏫 {c.mentorName}</span>
                    <span style={{ fontSize:'.72rem', color:'#16a34a', fontWeight:700 }}>₹{c.priceINR.toLocaleString()}</span>
                    <span style={{ fontSize:'.72rem', color:'#6b7280' }}>👥 {c.enrolledCount} enrolled</span>
                  </div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:6 }}>
                    {c.skills.map(s => <span key={s} style={{ background:'#f3f4f6', padding:'1px 8px', borderRadius:5, fontSize:'.65rem', fontWeight:700, color:'#374151' }}>{s}</span>)}
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, flexShrink:0, alignItems:'flex-start' }}>
                  <button className="btn btn-sm btn-outline">✏️ Edit</button>
                  <button className="btn btn-sm" style={{ background:'#fef2f2', color:'#ef4444', border:'1px solid #fecaca', borderRadius:7 }} onClick={() => deleteCourse(c.id)}>🗑 Remove</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'enrollments' && (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr>{['Mentee','Course','Week','Status','Enrolled','Actions'].map(h => (
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:'.65rem', fontWeight:800, textTransform:'uppercase', letterSpacing:'.07em', color:'#9ca3af', background:'#f9fafb', borderBottom:'1px solid #f3f4f6', whiteSpace:'nowrap' }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {enrollments.map(e => (
                  <tr key={e.id}>
                    <td style={{ padding:'12px 16px', fontWeight:700 }}>{e.mentee}</td>
                    <td style={{ padding:'12px 16px', color:'#6b7280', fontSize:'.84rem' }}>{e.course}</td>
                    <td style={{ padding:'12px 16px' }}>Week {e.week}</td>
                    <td style={{ padding:'12px 16px' }}>
                      <span className="st-badge" style={{ background: statusColor[e.status]+'22', color: statusColor[e.status], border:`1px solid ${statusColor[e.status]}44` }}>
                        {e.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={{ padding:'12px 16px', color:'#9ca3af', fontSize:'.78rem' }}>{e.enrolled}</td>
                    <td style={{ padding:'12px 16px' }}>
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-sm btn-outline">View</button>
                        {e.status === 'under_review' && <button className="btn btn-sm btn-primary">Review</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── EVENTS MANAGEMENT ──────────────────────────────────────── */
function EventsMgmt() {
  const [events, setEvents] = useState(EVENTS);
  const [showForm, setShowForm] = useState(false);
  const [platform, setPlatform] = useState('zoom');

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 className="db-page-title">📅 Events Management</h1>
          <p className="db-page-sub">Manage all mentor-hosted events, workshops, and webinars.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)} style={{ fontSize:'.84rem', padding:'9px 18px' }}>+ Create Event</button>
      </div>

      <div className="db-stats-grid-4" style={{ marginBottom:24 }}>
        {[
          { icon:'📅', label:'Total Events', v: events.length },
          { icon:'🔴', label:'Upcoming', v: events.filter(e=>e.status==='upcoming').length },
          { icon:'✅', label:'Completed', v: events.filter(e=>e.status==='completed').length },
          { icon:'👥', label:'Total Registrations', v: events.reduce((s,e) => s+e.registeredCount, 0) },
        ].map(s => (
          <div key={s.label} className="db-stat-card">
            <div className="dsc-icon">{s.icon}</div>
            <div className="dsc-label">{s.label}</div>
            <div className="dsc-value">{s.v}</div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="db-card" style={{ marginBottom:20 }}>
          <div style={{ padding:'14px 18px', borderBottom:'1px solid #f3f4f6', fontWeight:800, fontSize:'.9rem' }}>
            ➕ Create New Event
          </div>
          <div style={{ padding:'18px' }}>
            <div className="db-form-grid">
              <div className="db-form-field full"><label className="db-form-label">Event Title *</label><input className="db-form-input" placeholder="e.g. React Performance Masterclass" /></div>
              <div className="db-form-field full"><label className="db-form-label">Description *</label><textarea className="db-form-input" rows={3} style={{ width:'100%', resize:'vertical' }} placeholder="What will attendees learn?" /></div>
              <div className="db-form-field"><label className="db-form-label">Type</label>
                <select className="db-form-input"><option>workshop</option><option>webinar</option><option>office_hours</option><option>qa</option><option>hackathon</option></select>
              </div>
              <div className="db-form-field"><label className="db-form-label">Assign Mentor</label>
                <select className="db-form-input">{COURSES.map(c => <option key={c.id}>{c.mentorName}</option>)}</select>
              </div>
              <div className="db-form-field"><label className="db-form-label">Date</label><input className="db-form-input" type="date" /></div>
              <div className="db-form-field"><label className="db-form-label">Time</label><input className="db-form-input" type="time" /></div>
              <div className="db-form-field"><label className="db-form-label">Duration</label><input className="db-form-input" placeholder="60 minutes" /></div>
              <div className="db-form-field"><label className="db-form-label">Max Attendees</label><input className="db-form-input" type="number" defaultValue={100} /></div>
              <div className="db-form-field"><label className="db-form-label">Price (₹, 0 = free)</label><input className="db-form-input" type="number" defaultValue={0} /></div>

              {/* Meeting Platform */}
              <div className="db-form-field full">
                <label className="db-form-label">Meeting Platform</label>
                <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:10 }}>
                  {MEETING_PLATFORMS.map(p => (
                    <button key={p.id} type="button"
                      style={{ padding:'8px 14px', borderRadius:9, border:`2px solid ${platform === p.id ? p.color : '#e5e7eb'}`, background: platform === p.id ? p.color+'15' : '#fff', fontFamily:'inherit', fontWeight:700, fontSize:'.8rem', cursor:'pointer', color: platform === p.id ? p.color : '#6b7280', transition:'all .15s' }}
                      onClick={() => setPlatform(p.id)}
                    >
                      <PlatformIcon id={p.id} size={14} color={platform === p.id ? p.color : "#6b7280"} /> {p.label}
                    </button>
                  ))}
                </div>
                <input className="db-form-input" placeholder={MEETING_PLATFORMS.find(p2=>p2.id===platform)?.placeholder} />
              </div>

              <div className="db-form-field"><label className="db-form-label">LinkedIn Share URL</label><input className="db-form-input" placeholder="https://linkedin.com/posts/…" /></div>
              <div className="db-form-field"><label className="db-form-label">Twitter Share URL</label><input className="db-form-input" placeholder="https://twitter.com/intent/tweet?text=…" /></div>
              <div className="db-form-field full"><label className="db-form-label">Tags (comma-separated)</label><input className="db-form-input" placeholder="React, Performance, Frontend" /></div>
            </div>
            <div style={{ marginTop:14, display:'flex', gap:10 }}>
              <button className="btn btn-primary" style={{ fontSize:'.84rem', padding:'9px 18px' }} onClick={() => setShowForm(false)}>Create Event</button>
              <button onClick={() => setShowForm(false)} style={{ padding:'9px 18px', borderRadius:9, border:'1.5px solid #e5e7eb', background:'#fff', fontFamily:'inherit', fontSize:'.84rem', fontWeight:700, cursor:'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
        {events.map(e => (
          <div key={e.id} className="db-card">
            <div style={{ padding:'16px 18px', display:'flex', alignItems:'flex-start', gap:14, flexWrap:'wrap' }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:6, flexWrap:'wrap' }}>
                  <span className="st-badge st-mentor">{e.type.replace('_',' ')}</span>
                  <span className={`st-badge ${e.status === 'upcoming' ? 'st-upcoming' : e.status === 'completed' ? 'st-completed' : 'st-open'}`}>{e.status}</span>
                  {e.priceINR === 0 ? <span className="st-badge st-free">FREE</span> : <span style={{ fontSize:'.72rem', fontWeight:700, color:'#92400e', background:'#fef3c7', padding:'1px 8px', borderRadius:5 }}>₹{e.priceINR}</span>}
                </div>
                <div style={{ fontWeight:800, fontSize:'.9375rem', color:'#1a1a2e', marginBottom:4 }}>{e.title}</div>
                <div style={{ fontSize:'.78rem', color:'#6b7280', display:'flex', gap:12, flexWrap:'wrap' }}>
                  <span>👤 {e.mentor}</span>
                  <span>📅 {e.date} {e.time}</span>
                  <span>⏱ {e.duration}</span>
                  <span>👥 {e.registeredCount}/{e.maxAttendees} registered</span>
                  {e.rating && <span>⭐ {e.rating} ({e.totalRatings})</span>}
                </div>
              </div>
              <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                <button className="btn btn-sm btn-outline">✏️ Edit</button>
                <button className="btn btn-sm" style={{ background:'#fef2f2', color:'#ef4444', border:'1px solid #fecaca', borderRadius:7 }} onClick={() => setEvents(prev => prev.filter(ev => ev.id !== e.id))}>🗑 Remove</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── PLATFORM SETTINGS ──────────────────────────────────────── */
function Settings() {
  return (
    <div>
      <h1 className="db-page-title">⚙️ Platform Settings</h1>
      <p className="db-page-sub">Configure global platform rules, commission, cancellation policy, and SEO.</p>
      <div style={{ display:'flex', gap:10, marginBottom:20, justifyContent:'flex-end' }}>
        <button className="btn btn-primary" style={{ fontSize:'.84rem', padding:'9px 18px' }}>💾 Save All Settings</button>
      </div>
      {[
        { title:'💰 Payment & Commission', fields:[
          { label:'Platform Commission (%)', type:'number', default:15, hint:'Auto-deducted from each session payment' },
          { label:'Payout Cycle', type:'select', options:['Weekly','Bi-weekly','Monthly'] },
          { label:'Minimum Payout (₹)', type:'number', default:500 },
        ]},
        { title:'📅 Booking & Cancellation', fields:[
          { label:'Free Cancellation Window (hours)', type:'number', default:24 },
          { label:'Max Reschedules per Booking', type:'number', default:2 },
          { label:'Same-Day Booking Allowed', type:'toggle', default:false },
        ]},
        { title:'🎁 Free Session Policy', fields:[
          { label:'Free Sessions per New Mentee', type:'number', default:1 },
          { label:'Free Session Duration (min)', type:'number', default:30 },
        ]},
        { title:'🏆 Certification Policy', fields:[
          { label:'GitHub Submission Required', type:'toggle', default:true },
          { label:'Live Demo Review Required', type:'toggle', default:true },
          { label:'1:1 Q&A Before Certificate', type:'toggle', default:true },
        ]},
      ].map(section => (
        <div key={section.title} className="db-card" style={{ marginBottom:18 }}>
          <div style={{ padding:'12px 18px', borderBottom:'1px solid #f3f4f6', fontWeight:800, fontSize:'.9rem' }}>{section.title}</div>
          <div style={{ padding:'18px' }}>
            <div className="db-form-grid">
              {section.fields.map(f => (
                <div key={f.label} className="db-form-field">
                  <label className="db-form-label">{f.label}</label>
                  {f.type === 'select'
                    ? <select className="db-form-input">{f.options?.map(o => <option key={o}>{o}</option>)}</select>
                    : f.type === 'toggle'
                    ? <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                        <div style={{ width:46, height:25, borderRadius:100, background: f.default ? '#1DB954' : '#e5e7eb', cursor:'pointer', position:'relative', transition:'background .2s' }}>
                          <div style={{ position:'absolute', top:3, left: f.default ? 24 : 3, width:19, height:19, borderRadius:'50%', background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,.2)', transition:'left .2s' }} />
                        </div>
                        <span style={{ fontSize:'.8rem', color:'#6b7280', fontWeight:600 }}>{f.default ? 'Enabled' : 'Disabled'}</span>
                      </div>
                    : <input className="db-form-input" type={f.type} defaultValue={f.default} />
                  }
                  {f.hint && <span style={{ fontSize:'.68rem', color:'#9ca3af', marginTop:2 }}>{f.hint}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── EMAIL NOTIFICATIONS ───────────────────────────────────── */
function EmailAdmin() {
  const {
    config, updateConfig, saveConfig,
    templates, toggleTemplate, updateTemplate, resetTemplate, resetAllTemplates,
    addTemplate, deleteTemplate,
    sendTestEmail, emailLog,
  } = useEmail();

  const [tab,          setTab]          = useState('smtp');
  const [editId,       setEditId]       = useState(null);
  const [editDraft,    setEditDraft]    = useState({});
  const [testStatus,   setTestStatus]   = useState({});
  const [saved,        setSaved]        = useState(false);
  const [showPass,     setShowPass]     = useState(false);
  const [filterCat,    setFilterCat]    = useState('All');
  const [previewId,    setPreviewId]    = useState(null);
  const [showCreate,   setShowCreate]   = useState(false);
  const [createDraft,  setCreateDraft]  = useState({
    label:'', category:'Custom', to:'{{email}}', subject:'', html:'',
    variables:'email, firstName', previewVars:'',
  });
  const [createPreview,setCreatePreview]= useState(false);

  const categories = ['All', ...Array.from(new Set(templates.map(t => t.category)))];
  const visible = templates.filter(t => filterCat === 'All' || t.category === filterCat);

  const handleSave = () => { saveConfig(); setSaved(true); setTimeout(() => setSaved(false), 2500); };

  const handleTest = async (id) => {
    if (!config.testEmail) { alert('Set a test email address in SMTP Settings first.'); return; }
    setTestStatus(p => ({ ...p, [id]: 'sending' }));
    const ok = await sendTestEmail(id);
    setTestStatus(p => ({ ...p, [id]: ok ? 'sent' : 'error' }));
    setTimeout(() => setTestStatus(p => ({ ...p, [id]: 'idle' })), 3000);
  };

  const openEdit  = (t)  => { setEditId(t.id); setEditDraft({ subject: t.subject, html: t.html }); };
  const saveEdit  = ()   => { updateTemplate(editId, editDraft); setEditId(null); };
  const cancelEdit = ()  => { setEditId(null); setEditDraft({}); };

  // ── Reusable field ────────────────────────────────────────
  const Field = ({ label, value, onChange, type='text', hint, mono, required, placeholder }) => (
    <div style={{ marginBottom:14 }}>
      <label style={{ display:'block', fontSize:'.68rem', fontWeight:800, textTransform:'uppercase', letterSpacing:'.06em', color:'#374151', marginBottom:5 }}>
        {label} {required && <span style={{ color:'#ef4444' }}>*</span>}
      </label>
      <input
        type={type} value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width:'100%', padding:'9px 12px', border:`1.5px solid ${value ? '#1DB954' : '#e5e7eb'}`, borderRadius:9,
          fontFamily: mono ? 'monospace' : 'inherit', fontSize:'.83rem', outline:'none',
          background: value ? '#f0fdf4' : '#fff', color:'#111827', boxSizing:'border-box' }}
      />
      {hint && <div style={{ fontSize:'.65rem', color:'#9ca3af', marginTop:3 }}>{hint}</div>}
      {value && <div style={{ fontSize:'.65rem', color:'#16a34a', fontWeight:700, marginTop:3 }}>✅ Set</div>}
    </div>
  );

  const Toggle = ({ enabled, onToggle }) => (
    <div onClick={onToggle} style={{ width:44, height:24, borderRadius:100, cursor:'pointer',
      background: enabled ? '#1DB954' : '#e5e7eb', position:'relative', transition:'background .2s', flexShrink:0 }}>
      <div style={{ position:'absolute', top:3, left: enabled ? 21 : 3, width:18, height:18,
        borderRadius:'50%', background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,.25)', transition:'left .2s' }} />
    </div>
  );

  // ── Category colour ───────────────────────────────────────
  const catColor = { Auth:'#2563eb', Booking:'#1DB954', Payment:'#f59e0b', Certification:'#7c3aed', Mentor:'#0891b2', Events:'#8b5cf6', Support:'#ef4444' };
  const catIcon  = { Auth:'🔐', Booking:'📅', Payment:'💳', Certification:'🏆', Mentor:'💼', Events:'🎤', Support:'🎫' };

  // ── Email preview modal ───────────────────────────────────
  const prevTmpl = templates.find(t => t.id === previewId);
  const renderPreview = (tmpl) => {
    if (!tmpl) return '';
    return Object.entries(tmpl.preview || {}).reduce((s, [k,v]) => s.replaceAll(`{{${k}}}`, v), tmpl.html);
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 className="db-page-title">📧 Email Notifications</h1>
          <p className="db-page-sub">Configure SMTP settings, manage {templates.length} email templates, and view the send log.</p>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <button onClick={handleSave} style={{ padding:'9px 20px', borderRadius:9, border:'none',
            background: saved ? '#16a34a' : '#1DB954', color:'#fff', fontFamily:'inherit',
            fontSize:'.82rem', fontWeight:800, cursor:'pointer', transition:'background .2s' }}>
            {saved ? '✅ Saved!' : '💾 Save All'}
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:24 }}>
        {[
          { label:'Total Templates', value:templates.length,                         color:'#1DB954', icon:'📧' },
          { label:'Enabled',         value:templates.filter(t=>t.enabled).length,    color:'#16a34a', icon:'✅' },
          { label:'Disabled',        value:templates.filter(t=>!t.enabled).length,   color:'#ef4444', icon:'⏸' },
          { label:'Emails Logged',   value:emailLog.length,                          color:'#7c3aed', icon:'📨' },
        ].map(s => (
          <div key={s.label} className="db-card" style={{ padding:'16px 18px', textAlign:'center', borderTop:`3px solid ${s.color}` }}>
            <div style={{ fontSize:'1.5rem' }}>{s.icon}</div>
            <div style={{ fontSize:'1.6rem', fontWeight:900, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:'.7rem', fontWeight:700, color:'#9ca3af', textTransform:'uppercase' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:'2px solid #f3f4f6', marginBottom:24, gap:0 }}>
        {[
          { id:'smtp',      label:'⚙️ SMTP & Config' },
          { id:'templates', label:`📋 Templates (${templates.length})` },
          { id:'log',       label:`📨 Send Log (${emailLog.length})` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding:'10px 22px', border:'none', borderBottom: tab===t.id ? '2px solid #1DB954' : '2px solid transparent',
            background:'none', fontFamily:'inherit', fontSize:'.84rem', fontWeight: tab===t.id ? 800 : 600,
            color: tab===t.id ? '#1DB954' : '#6b7280', cursor:'pointer', marginBottom:-2, transition:'all .15s',
          }}>{t.label}</button>
        ))}
      </div>

      {/* ══════════════════════════════════════
          SMTP & CONFIGURATION TAB
          ══════════════════════════════════════ */}
      {tab === 'smtp' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>

          {/* Left: SMTP */}
          <div className="db-card">
            <div style={{ padding:'14px 18px', borderBottom:'1px solid #f3f4f6', fontWeight:800, fontSize:'.9rem', display:'flex', alignItems:'center', gap:8 }}>
              🖥️ SMTP Server Settings
              <span style={{ marginLeft:'auto', padding:'2px 8px', background:'#fef3c7', color:'#92400e', fontSize:'.6rem', fontWeight:900, borderRadius:4 }}>TODO — Add credentials</span>
            </div>
            <div style={{ padding:'18px' }}>
              {/* Provider picker */}
              <div style={{ marginBottom:16 }}>
                <label style={{ display:'block', fontSize:'.68rem', fontWeight:800, textTransform:'uppercase', color:'#374151', marginBottom:8 }}>Email Provider</label>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                  {[
                    { id:'smtp',     label:'Custom SMTP',  icon:'🖥️', hint:'Gmail, Outlook…'  },
                    { id:'ses',      label:'Amazon SES',   icon:'☁️', hint:'Low-cost, scalable' },
                    { id:'sendgrid', label:'SendGrid',     icon:'📤', hint:'100/day free'      },
                    { id:'brevo',    label:'Brevo',        icon:'💌', hint:'300/day free'      },
                  ].map(p => (
                    <button key={p.id} onClick={() => updateConfig({ provider: p.id })}
                      style={{ padding:'10px', borderRadius:9, border:`1.5px solid ${config.provider===p.id?'#1DB954':'#e5e7eb'}`,
                        background: config.provider===p.id ? '#f0fdf4' : '#fff', cursor:'pointer',
                        fontFamily:'inherit', textAlign:'left', transition:'all .15s' }}>
                      <div style={{ fontSize:'.78rem', fontWeight:800, color: config.provider===p.id?'#16a34a':'#374151' }}>{p.icon} {p.label}</div>
                      <div style={{ fontSize:'.65rem', color:'#9ca3af', marginTop:2 }}>{p.hint}</div>
                    </button>
                  ))}
                </div>
              </div>

              <Field label="SMTP Host" value={config.smtpHost} onChange={v=>updateConfig({smtpHost:v})} hint="e.g. smtp.gmail.com or smtp.sendgrid.net" placeholder="smtp.gmail.com" />

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
                <div>
                  <label style={{ display:'block', fontSize:'.68rem', fontWeight:800, textTransform:'uppercase', color:'#374151', marginBottom:5 }}>Port</label>
                  <select value={config.smtpPort} onChange={e=>updateConfig({smtpPort:+e.target.value})}
                    style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e5e7eb', borderRadius:9, fontFamily:'inherit', fontSize:'.83rem' }}>
                    <option value={587}>587 (TLS — recommended)</option>
                    <option value={465}>465 (SSL)</option>
                    <option value={25}>25 (plain — not recommended)</option>
                  </select>
                </div>
                <div style={{ paddingTop:22 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <Toggle enabled={config.smtpSecure} onToggle={()=>updateConfig({smtpSecure:!config.smtpSecure})} />
                    <span style={{ fontSize:'.8rem', fontWeight:700, color:config.smtpSecure?'#16a34a':'#9ca3af' }}>SSL/TLS</span>
                  </div>
                </div>
              </div>

              <Field label="SMTP Username" value={config.smtpUser} onChange={v=>updateConfig({smtpUser:v})} placeholder="noreply@devbrains.in or API key username" mono />

              <div style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:'.68rem', fontWeight:800, textTransform:'uppercase', color:'#374151', marginBottom:5 }}>
                  SMTP Password / App Password <span style={{ background:'#fef3c7', color:'#92400e', padding:'1px 5px', borderRadius:4, fontSize:'.58rem' }}>SECRET</span>
                </label>
                <div style={{ position:'relative', display:'flex' }}>
                  <input type={showPass?'text':'password'} value={config.smtpPass||''} onChange={e=>updateConfig({smtpPass:e.target.value})}
                    placeholder="Gmail: use App Password, not your real password"
                    style={{ flex:1, padding:'9px 44px 9px 12px', border:`1.5px solid ${config.smtpPass?'#1DB954':'#e5e7eb'}`,
                      borderRadius:9, fontFamily:'monospace', fontSize:'.83rem', background: config.smtpPass?'#f0fdf4':'#fff' }} />
                  <button onClick={()=>setShowPass(p=>!p)} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
                    background:'none', border:'none', cursor:'pointer', color:'#9ca3af', fontSize:'.8rem' }}>
                    {showPass?'🙈':'👁'}
                  </button>
                </div>
                <div style={{ fontSize:'.65rem', color:'#9ca3af', marginTop:3 }}>⚠️ For Gmail, enable 2FA and generate an App Password at myaccount.google.com</div>
              </div>
            </div>
          </div>

          {/* Right: Sender + Options */}
          <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
            <div className="db-card">
              <div style={{ padding:'14px 18px', borderBottom:'1px solid #f3f4f6', fontWeight:800, fontSize:'.9rem' }}>✉️ Sender Identity</div>
              <div style={{ padding:'18px' }}>
                <Field label="From Name" value={config.fromName} onChange={v=>updateConfig({fromName:v})} placeholder="DevBrains" required />
                <Field label="From Email" value={config.fromEmail} onChange={v=>updateConfig({fromEmail:v})} placeholder="noreply@devbrains.in" required />
                <Field label="Reply-To Email" value={config.replyTo} onChange={v=>updateConfig({replyTo:v})} placeholder="support@devbrains.in" hint="Users replying will reach this address" />
                <Field label="Test Email Address" value={config.testEmail} onChange={v=>updateConfig({testEmail:v})} placeholder="your.email@gmail.com" hint="Send test emails to this address using the 🧪 button on each template" />
              </div>
            </div>

            <div className="db-card">
              <div style={{ padding:'14px 18px', borderBottom:'1px solid #f3f4f6', fontWeight:800, fontSize:'.9rem' }}>🔧 Global Options</div>
              <div style={{ padding:'18px' }}>
                {[
                  { label:'Email Notifications',   hint:'Master on/off switch for ALL emails', key:'emailEnabled' },
                  { label:'Calendar Invites (.ics)',hint:'Attach calendar event to booking emails', key:'calendarInvites' },
                  { label:'Unsubscribe Footer',    hint:'Add unsubscribe link to all marketing emails', key:'unsubscribeLink' },
                ].map(o => (
                  <div key={o.key} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 0', borderBottom:'1px solid #f9fafb' }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:'.85rem', color:'#111827' }}>{o.label}</div>
                      <div style={{ fontSize:'.7rem', color:'#9ca3af' }}>{o.hint}</div>
                    </div>
                    <Toggle enabled={config[o.key]} onToggle={() => updateConfig({ [o.key]: !config[o.key] })} />
                  </div>
                ))}
              </div>
            </div>

            {/* Setup guide */}
            <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:12, padding:16 }}>
              <div style={{ fontWeight:800, color:'#1e40af', fontSize:'.8rem', marginBottom:10 }}>📖 SMTP Setup Guides</div>
              {[
                { name:'Gmail',    hint:'Enable 2FA → App Passwords → generate one',          host:'smtp.gmail.com', port:'587', link:'https://myaccount.google.com/apppasswords' },
                { name:'Brevo',    hint:'Free 300 emails/day, great for startups',             host:'smtp-relay.brevo.com', port:'587', link:'https://app.brevo.com/settings/keys/smtp' },
                { name:'SendGrid', hint:'100 emails/day free, reliable delivery',             host:'smtp.sendgrid.net', port:'587', link:'https://app.sendgrid.com/settings/api_keys' },
                { name:'AWS SES',  hint:'Very low cost at scale — requires domain verification',host:'email-smtp.ap-south-1.amazonaws.com',port:'587',link:'https://console.aws.amazon.com/ses' },
              ].map(g => (
                <div key={g.name} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 0', borderBottom:'1px solid #bfdbfe' }}>
                  <div>
                    <span style={{ fontWeight:700, fontSize:'.78rem', color:'#1e40af' }}>{g.name}</span>
                    <span style={{ fontSize:'.68rem', color:'#3b82f6', marginLeft:6 }}>— {g.hint}</span>
                  </div>
                  <button onClick={()=>{updateConfig({smtpHost:g.host,smtpPort:+g.port});}}
                    style={{ padding:'4px 10px', borderRadius:6, border:'1px solid #bfdbfe', background:'#dbeafe', cursor:'pointer', fontFamily:'inherit', fontSize:'.68rem', fontWeight:700, color:'#1e40af' }}>
                    Use →
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
          TEMPLATES TAB
          ══════════════════════════════════════ */}
      {tab === 'templates' && (
        <div>
          {/* Filter bar */}
          <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {categories.map(c => (
                <button key={c} onClick={()=>setFilterCat(c)} style={{ padding:'6px 14px', borderRadius:100, border:`1.5px solid ${filterCat===c?(catColor[c]||'#1DB954'):'#e5e7eb'}`,
                  background: filterCat===c ? (catColor[c]||'#1DB954') + '18' : '#fff',
                  color: filterCat===c ? (catColor[c]||'#1DB954') : '#6b7280',
                  fontFamily:'inherit', fontSize:'.75rem', fontWeight:700, cursor:'pointer' }}>
                  {c !== 'All' ? (catIcon[c]||'') + ' ' : ''}{c}
                  <span style={{ marginLeft:4, background:'#f3f4f6', padding:'0 5px', borderRadius:100, fontSize:'.65rem' }}>
                    {c==='All' ? templates.length : templates.filter(t=>t.category===c).length}
                  </span>
                </button>
              ))}
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => { setShowCreate(true); setCreateDraft({ label:'', category:'Custom', to:'{{email}}', subject:'', html: '<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:32px">\n  <h2 style="color:#1a1a2e">Hello {{firstName}},</h2>\n  <p style="color:#374151;line-height:1.7">Your message here.</p>\n  <a href="{{ctaUrl}}" style="display:inline-block;background:#1DB954;color:#fff;padding:12px 24px;border-radius:8px;text-decoration:none;font-weight:700">Click Here →</a>\n  <p style="color:#9ca3af;font-size:12px;margin-top:24px">DevBrains · www.devbrains.in</p>\n</div>', variables:'email, firstName', previewVars:'{"email":"test@example.com","firstName":"Rahul","ctaUrl":"https://devbrains.in"}' }); }}
                style={{ padding:'6px 16px', borderRadius:8, border:'none', background:'#1DB954', color:'#fff', fontFamily:'inherit', fontSize:'.78rem', fontWeight:800, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                ＋ Create Template
              </button>
              <button onClick={resetAllTemplates} style={{ padding:'6px 12px', borderRadius:8, border:'1.5px solid #e5e7eb', background:'#fff', fontFamily:'inherit', fontSize:'.72rem', fontWeight:700, color:'#6b7280', cursor:'pointer' }}>
                ↺ Reset Built-ins
              </button>
            </div>
          </div>

          {/* ── Create Template Modal ── */}
          {showCreate && (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
              onClick={() => setShowCreate(false)}>
              <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:820, maxHeight:'94vh', display:'flex', flexDirection:'column', overflow:'hidden' }}
                onClick={e => e.stopPropagation()}>

                {/* Modal header */}
                <div style={{ padding:'18px 24px', background:'linear-gradient(135deg,#1a1a2e,#16213e)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div>
                    <div style={{ color:'#1DB954', fontWeight:900, fontSize:'1.05rem', letterSpacing:'-.3px' }}>✉️ Create Email Template</div>
                    <div style={{ color:'rgba(255,255,255,.55)', fontSize:'.72rem', marginTop:2 }}>Custom templates are saved alongside built-in templates</div>
                  </div>
                  <div style={{ display:'flex', gap:10 }}>
                    <button onClick={() => setCreatePreview(p => !p)}
                      style={{ padding:'6px 14px', borderRadius:8, border:'1.5px solid rgba(255,255,255,.2)', background: createPreview ? 'rgba(29,185,84,.25)' : 'transparent', color: createPreview ? '#1DB954' : 'rgba(255,255,255,.7)', fontFamily:'inherit', fontSize:'.75rem', fontWeight:700, cursor:'pointer' }}>
                      {createPreview ? '✏️ Edit' : '👁 Preview'}
                    </button>
                    <button onClick={() => setShowCreate(false)}
                      style={{ background:'rgba(255,255,255,.12)', border:'none', borderRadius:'50%', width:30, height:30, color:'#fff', cursor:'pointer', fontSize:'1rem', display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
                  </div>
                </div>

                {/* Modal body */}
                <div style={{ flex:1, overflowY:'auto', display:'grid', gridTemplateColumns: createPreview ? '1fr 1fr' : '1fr', gap:0 }}>
                  {/* Left: form */}
                  <div style={{ padding:'24px', borderRight: createPreview ? '1px solid #e5e7eb' : 'none' }}>
                    {/* Row 1: Label + Category */}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                      <div>
                        <label style={{ display:'block', fontSize:'.68rem', fontWeight:800, textTransform:'uppercase', color:'#374151', marginBottom:5 }}>Template Name <span style={{ color:'#ef4444' }}>*</span></label>
                        <input value={createDraft.label} onChange={e => setCreateDraft(p => ({ ...p, label:e.target.value }))}
                          placeholder="e.g. 🎁 Weekly Digest Email"
                          style={{ width:'100%', padding:'9px 12px', border:`1.5px solid ${createDraft.label?'#1DB954':'#e5e7eb'}`, borderRadius:9, fontFamily:'inherit', fontSize:'.84rem', boxSizing:'border-box' }} />
                      </div>
                      <div>
                        <label style={{ display:'block', fontSize:'.68rem', fontWeight:800, textTransform:'uppercase', color:'#374151', marginBottom:5 }}>Category</label>
                        <select value={createDraft.category} onChange={e => setCreateDraft(p => ({ ...p, category:e.target.value }))}
                          style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e5e7eb', borderRadius:9, fontFamily:'inherit', fontSize:'.84rem', boxSizing:'border-box' }}>
                          {['Auth','Booking','Payment','Certification','Mentor','Events','Support','Custom','Marketing','Transactional'].map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Row 2: To + Subject */}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:14, marginBottom:14 }}>
                      <div>
                        <label style={{ display:'block', fontSize:'.68rem', fontWeight:800, textTransform:'uppercase', color:'#374151', marginBottom:5 }}>Recipient (To)</label>
                        <input value={createDraft.to} onChange={e => setCreateDraft(p => ({ ...p, to:e.target.value }))}
                          placeholder="{{email}}"
                          style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e5e7eb', borderRadius:9, fontFamily:'monospace', fontSize:'.8rem', boxSizing:'border-box' }} />
                      </div>
                      <div>
                        <label style={{ display:'block', fontSize:'.68rem', fontWeight:800, textTransform:'uppercase', color:'#374151', marginBottom:5 }}>Subject Line <span style={{ color:'#ef4444' }}>*</span></label>
                        <input value={createDraft.subject} onChange={e => setCreateDraft(p => ({ ...p, subject:e.target.value }))}
                          placeholder="e.g. 🎁 Your weekly DevBrains digest, {{firstName}}!"
                          style={{ width:'100%', padding:'9px 12px', border:`1.5px solid ${createDraft.subject?'#1DB954':'#e5e7eb'}`, borderRadius:9, fontFamily:'inherit', fontSize:'.84rem', boxSizing:'border-box' }} />
                      </div>
                    </div>

                    {/* Variables hint */}
                    <div style={{ marginBottom:10 }}>
                      <label style={{ display:'block', fontSize:'.68rem', fontWeight:800, textTransform:'uppercase', color:'#374151', marginBottom:5 }}>Template Variables <span style={{ fontSize:'.65rem', fontWeight:500, textTransform:'none', color:'#9ca3af' }}>(comma-separated names)</span></label>
                      <input value={createDraft.variables} onChange={e => setCreateDraft(p => ({ ...p, variables:e.target.value }))}
                        placeholder="email, firstName, lastName, ctaUrl"
                        style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e5e7eb', borderRadius:9, fontFamily:'monospace', fontSize:'.8rem', boxSizing:'border-box' }} />
                      <div style={{ fontSize:'.65rem', color:'#9ca3af', marginTop:3 }}>Use these in your HTML as {'{{variableName}}'} — they will be replaced with real values when sending.</div>
                    </div>

                    {/* Quick insert variables */}
                    {createDraft.variables && (
                      <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginBottom:14 }}>
                        {createDraft.variables.split(',').map(v => v.trim()).filter(Boolean).map(v => (
                          <button key={v} onClick={() => {
                            const tag = `{{${v}}}`;
                            setCreateDraft(p => ({ ...p, html: p.html + tag }));
                          }}
                            style={{ padding:'3px 9px', borderRadius:6, border:'1px dashed #1DB954', background:'#f0fdf4', color:'#16a34a', fontFamily:'monospace', fontSize:'.68rem', cursor:'pointer', fontWeight:700 }}>
                            + {'{{' + v + '}}'}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* HTML editor */}
                    <div style={{ marginBottom:14 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
                        <label style={{ display:'block', fontSize:'.68rem', fontWeight:800, textTransform:'uppercase', color:'#374151' }}>HTML Body <span style={{ color:'#ef4444' }}>*</span></label>
                        <div style={{ display:'flex', gap:6 }}>
                          {/* Quick starter snippets */}
                          {[
                            { label:'Green Header', snippet: '<div style="background:#1DB954;padding:32px;text-align:center;border-radius:16px 16px 0 0"><h1 style="color:#fff;margin:0">Your Title Here</h1></div>' },
                            { label:'Info Table', snippet: '\n<table style="width:100%;border-collapse:collapse;margin:16px 0"><tr><td style="padding:10px;background:#f9fafb;font-weight:700;width:40%">Label</td><td style="padding:10px">Value</td></tr></table>' },
                            { label:'CTA Button', snippet: '\n<p style="text-align:center"><a href="{{ctaUrl}}" style="display:inline-block;background:#1DB954;color:#fff;padding:12px 28px;border-radius:10px;text-decoration:none;font-weight:800">Click Here →</a></p>' },
                            { label:'Alert Box', snippet: '\n<div style="background:#fef2f2;border:1px solid #fecaca;border-radius:10px;padding:14px;margin:12px 0"><p style="margin:0;color:#991b1b">⚠️ Important message here</p></div>' },
                          ].map(s => (
                            <button key={s.label} onClick={() => setCreateDraft(p => ({ ...p, html: p.html + s.snippet }))}
                              style={{ padding:'3px 9px', borderRadius:6, border:'1px solid #e5e7eb', background:'#f9fafb', fontFamily:'inherit', fontSize:'.65rem', cursor:'pointer', color:'#6b7280' }}>
                              + {s.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <textarea value={createDraft.html} onChange={e => setCreateDraft(p => ({ ...p, html:e.target.value }))} rows={14}
                        placeholder="<div style='font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:32px'>&#10;  <h2>Hello {{firstName}},</h2>&#10;  <p>Your message here...</p>&#10;</div>"
                        style={{ width:'100%', padding:'10px 12px', border:`1.5px solid ${createDraft.html?'#1DB954':'#e5e7eb'}`, borderRadius:9, fontFamily:'monospace', fontSize:'.74rem', lineHeight:1.6, resize:'vertical', outline:'none', boxSizing:'border-box' }} />
                    </div>

                    {/* Preview data */}
                    <div>
                      <label style={{ display:'block', fontSize:'.68rem', fontWeight:800, textTransform:'uppercase', color:'#374151', marginBottom:5 }}>Preview Data (JSON) <span style={{ fontSize:'.65rem', fontWeight:500, textTransform:'none', color:'#9ca3af' }}>(for 🧪 Test and 👁 Preview)</span></label>
                      <textarea value={createDraft.previewVars} onChange={e => setCreateDraft(p => ({ ...p, previewVars:e.target.value }))} rows={3}
                        placeholder='{"email":"test@example.com","firstName":"Rahul","ctaUrl":"https://devbrains.in"}'
                        style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e5e7eb', borderRadius:9, fontFamily:'monospace', fontSize:'.74rem', resize:'vertical', boxSizing:'border-box' }} />
                    </div>
                  </div>

                  {/* Right: live preview */}
                  {createPreview && (
                    <div style={{ padding:16, background:'#f3f4f6', overflowY:'auto' }}>
                      <div style={{ fontSize:'.68rem', fontWeight:800, textTransform:'uppercase', color:'#9ca3af', marginBottom:12, letterSpacing:'.07em' }}>Live Preview</div>
                      {createDraft.subject && (
                        <div style={{ background:'#fff', borderRadius:8, padding:'8px 12px', marginBottom:12, fontSize:'.78rem', color:'#374151', fontWeight:600, border:'1px solid #e5e7eb' }}>
                          <span style={{ color:'#9ca3af', fontWeight:400 }}>Subject: </span>{createDraft.subject}
                        </div>
                      )}
                      <div style={{ background:'#fff', borderRadius:12, overflow:'hidden', boxShadow:'0 2px 12px rgba(0,0,0,.07)' }}>
                        <div dangerouslySetInnerHTML={{ __html: (() => {
                          let html = createDraft.html || '';
                          try {
                            const vars = JSON.parse(createDraft.previewVars || '{}');
                            html = Object.entries(vars).reduce((s, [k,v]) => s.replaceAll(`{{${k}}}`, v), html);
                          } catch {}
                          return html;
                        })() }} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Modal footer */}
                <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e7eb', display:'flex', gap:10, alignItems:'center', background:'#f9fafb' }}>
                  <button
                    onClick={() => {
                      if (!createDraft.label || !createDraft.subject || !createDraft.html) {
                        alert('Please fill in Name, Subject, and HTML Body.');
                        return;
                      }
                      let preview = {};
                      try { preview = JSON.parse(createDraft.previewVars || '{}'); } catch {}
                      addTemplate({
                        label:     createDraft.label,
                        category:  createDraft.category,
                        to:        createDraft.to,
                        subject:   createDraft.subject,
                        html:      createDraft.html,
                        variables: createDraft.variables.split(',').map(v => v.trim()).filter(Boolean),
                        preview,
                      });
                      setShowCreate(false);
                      setFilterCat('All');
                    }}
                    style={{ padding:'10px 28px', borderRadius:9, border:'none', background:'#1DB954', color:'#fff', fontFamily:'inherit', fontWeight:900, fontSize:'.88rem', cursor:'pointer' }}>
                    ✅ Save Template
                  </button>
                  <button onClick={() => setCreatePreview(p => !p)}
                    style={{ padding:'10px 18px', borderRadius:9, border:'1.5px solid #e5e7eb', background:'#fff', fontFamily:'inherit', fontWeight:700, fontSize:'.84rem', cursor:'pointer', color:'#374151' }}>
                    {createPreview ? '✏️ Editor Only' : '👁 Show Preview'}
                  </button>
                  <span style={{ flex:1 }} />
                  <button onClick={() => setShowCreate(false)}
                    style={{ padding:'10px 16px', borderRadius:9, border:'1.5px solid #e5e7eb', background:'#fff', fontFamily:'inherit', fontWeight:700, cursor:'pointer', color:'#6b7280' }}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Template cards */}
          {visible.map(t => {
            const ts = testStatus[t.id] || 'idle';
            const isEditing = editId === t.id;
            return (
              <div key={t.id} className="db-card" style={{ marginBottom:12, border:`1.5px solid ${t.enabled?(catColor[t.category]||'#1DB954')+'22':'#f3f4f6'}`, transition:'border .2s', opacity: t.enabled?1:.65 }}>
                {/* Row */}
                <div style={{ padding:'14px 18px', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                  {/* Category badge */}
                  <div style={{ padding:'3px 10px', borderRadius:100, background:(catColor[t.category]||'#6b7280')+'15', color:(catColor[t.category]||'#6b7280'), fontSize:'.65rem', fontWeight:800, whiteSpace:'nowrap' }}>
                    {catIcon[t.category]||'📧'} {t.category}
                  </div>
                  {t.isCustom && (
                    <div style={{ padding:'3px 9px', borderRadius:100, background:'#fef3c7', color:'#92400e', fontSize:'.62rem', fontWeight:800, whiteSpace:'nowrap' }}>
                      ✏️ Custom
                    </div>
                  )}
                  {/* Label + subject preview */}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:800, fontSize:'.88rem', color:'#111827', marginBottom:2 }}>{t.label}</div>
                    <div style={{ fontSize:'.72rem', color:'#9ca3af', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      To: <code style={{ background:'#f3f4f6', padding:'1px 5px', borderRadius:4 }}>{t.to}</code> · Subject: {t.subject}
                    </div>
                  </div>
                  {/* Actions */}
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                    <Toggle enabled={t.enabled} onToggle={()=>toggleTemplate(t.id)} />
                    <button onClick={() => setPreviewId(t.id)}
                      style={{ padding:'6px 12px', borderRadius:7, border:'1.5px solid #e5e7eb', background:'#fff', cursor:'pointer', fontFamily:'inherit', fontSize:'.72rem', fontWeight:700, color:'#374151' }}>
                      👁 Preview
                    </button>
                    <button onClick={() => openEdit(t)}
                      style={{ padding:'6px 12px', borderRadius:7, border:'1.5px solid #e5e7eb', background:'#fff', cursor:'pointer', fontFamily:'inherit', fontSize:'.72rem', fontWeight:700, color:'#374151' }}>
                      ✏️ Edit
                    </button>
                    <button onClick={() => handleTest(t.id)} disabled={ts==='sending'}
                      style={{ padding:'6px 12px', borderRadius:7, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:'.72rem', fontWeight:800,
                        background: ts==='sent'?'#16a34a':ts==='error'?'#ef4444':ts==='sending'?'#9ca3af':'#1DB954', color:'#fff', transition:'background .2s' }}>
                      {ts==='sending'?'⏳…':ts==='sent'?'✅ Sent!':ts==='error'?'❌ Error':'🧪 Test'}
                    </button>
                    {t.isCustom ? (
                      <button onClick={() => { if(window.confirm(`Delete "${t.label}"? This cannot be undone.`)) deleteTemplate(t.id); }}
                        style={{ padding:'6px 10px', borderRadius:7, border:'1.5px solid #fecaca', background:'#fef2f2', cursor:'pointer', fontFamily:'inherit', fontSize:'.7rem', color:'#dc2626', fontWeight:700 }}
                        title="Delete custom template">
                        🗑
                      </button>
                    ) : (
                      <button onClick={() => resetTemplate(t.id)}
                        style={{ padding:'6px 10px', borderRadius:7, border:'1.5px solid #e5e7eb', background:'#fff', cursor:'pointer', fontFamily:'inherit', fontSize:'.7rem', color:'#9ca3af' }}
                        title="Reset to default">
                        ↺
                      </button>
                    )}
                  </div>
                </div>

                {/* Inline editor */}
                {isEditing && (
                  <div style={{ borderTop:'1px solid #f3f4f6', padding:'18px', background:'#f9fafb' }}>
                    <div style={{ marginBottom:14 }}>
                      <label style={{ display:'block', fontSize:'.68rem', fontWeight:800, textTransform:'uppercase', color:'#374151', marginBottom:5 }}>Subject Line</label>
                      <input value={editDraft.subject||''} onChange={e=>setEditDraft(p=>({...p,subject:e.target.value}))}
                        style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #1DB954', borderRadius:9, fontFamily:'inherit', fontSize:'.83rem', outline:'none', boxSizing:'border-box' }} />
                      <div style={{ fontSize:'.65rem', color:'#9ca3af', marginTop:3 }}>
                        Available variables: {t.variables?.map(v => `{{${v}}}`).join(', ')}
                      </div>
                    </div>
                    <div style={{ marginBottom:14 }}>
                      <label style={{ display:'block', fontSize:'.68rem', fontWeight:800, textTransform:'uppercase', color:'#374151', marginBottom:5 }}>HTML Body</label>
                      <textarea value={editDraft.html||''} onChange={e=>setEditDraft(p=>({...p,html:e.target.value}))} rows={12}
                        style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #e5e7eb', borderRadius:9, fontFamily:'monospace', fontSize:'.75rem', outline:'none', resize:'vertical', boxSizing:'border-box' }} />
                    </div>
                    <div style={{ display:'flex', gap:10 }}>
                      <button onClick={saveEdit} style={{ padding:'8px 20px', borderRadius:8, border:'none', background:'#1DB954', color:'#fff', fontFamily:'inherit', fontWeight:800, cursor:'pointer' }}>Save Template</button>
                      <button onClick={cancelEdit} style={{ padding:'8px 16px', borderRadius:8, border:'1.5px solid #e5e7eb', background:'#fff', fontFamily:'inherit', fontWeight:700, cursor:'pointer', color:'#6b7280' }}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ══════════════════════════════════════
          SEND LOG TAB
          ══════════════════════════════════════ */}
      {tab === 'log' && (
        <div>
          {emailLog.length === 0 ? (
            <div style={{ textAlign:'center', padding:'60px 20px', color:'#9ca3af' }}>
              <div style={{ fontSize:'2.5rem', marginBottom:12 }}>📭</div>
              <div style={{ fontWeight:700, fontSize:'1rem', marginBottom:6 }}>No emails sent yet</div>
              <div style={{ fontSize:'.8rem' }}>Trigger an action (register, book a session) to see emails logged here.</div>
            </div>
          ) : (
            <div className="db-card">
              <div style={{ padding:'12px 18px', borderBottom:'1px solid #f3f4f6', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontWeight:800, fontSize:'.88rem' }}>Last {emailLog.length} emails</span>
                <span style={{ fontSize:'.72rem', color:'#9ca3af' }}>Cleared on page refresh</span>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr>{['Status','Template','To','Subject','Time'].map(h=>(
                      <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontSize:'.65rem', fontWeight:800, textTransform:'uppercase', letterSpacing:'.07em', color:'#9ca3af', background:'#f9fafb', borderBottom:'1px solid #f3f4f6', whiteSpace:'nowrap' }}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {emailLog.map(l => (
                      <tr key={l.id} style={{ borderBottom:'1px solid #f9fafb' }}>
                        <td style={{ padding:'10px 14px' }}>
                          <span style={{ padding:'3px 9px', borderRadius:100, fontSize:'.65rem', fontWeight:800,
                            background: l.status==='sent'?'#f0fdf4':l.status==='pending'?'#fffbeb':'#fef2f2',
                            color:      l.status==='sent'?'#16a34a':l.status==='pending'?'#b45309':'#dc2626' }}>
                            {l.status==='sent'?'✅ Sent':l.status==='pending'?'⏳ Pending':'❌ Failed'}
                          </span>
                        </td>
                        <td style={{ padding:'10px 14px', fontSize:'.78rem', fontWeight:700, color:'#374151' }}>{l.label}</td>
                        <td style={{ padding:'10px 14px', fontSize:'.75rem', color:'#6b7280', fontFamily:'monospace' }}>{l.to}</td>
                        <td style={{ padding:'10px 14px', fontSize:'.75rem', color:'#374151', maxWidth:240, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.subject}</td>
                        <td style={{ padding:'10px 14px', fontSize:'.72rem', color:'#9ca3af', whiteSpace:'nowrap' }}>
                          {new Date(l.ts).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Preview Modal ── */}
      {previewId && prevTmpl && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
          onClick={() => setPreviewId(null)}>
          <div style={{ background:'#f3f4f6', borderRadius:16, width:'100%', maxWidth:660, maxHeight:'90vh', display:'flex', flexDirection:'column', overflow:'hidden' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding:'14px 20px', background:'#1a1a2e', display:'flex', justifyContent:'space-between', alignItems:'center', borderRadius:'16px 16px 0 0' }}>
              <div>
                <div style={{ color:'#fff', fontWeight:800, fontSize:'.9rem' }}>{prevTmpl.label}</div>
                <div style={{ color:'#9ca3af', fontSize:'.72rem', marginTop:2 }}>Subject: {prevTmpl.subject}</div>
              </div>
              <button onClick={()=>setPreviewId(null)} style={{ background:'rgba(255,255,255,.15)', border:'none', borderRadius:'50%', width:30, height:30, color:'#fff', cursor:'pointer', fontSize:'1rem' }}>✕</button>
            </div>
            <div style={{ flex:1, overflowY:'auto', padding:16 }}>
              <div style={{ background:'#fff', borderRadius:12, overflow:'hidden' }}
                dangerouslySetInnerHTML={{ __html: renderPreview(prevTmpl) }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── INTEGRATIONS ──────────────────────────────────────────── */
function Integrations() {
  const {
    meetingPlatforms, toggleMeetingPlatform, updateMeetingConfig,
    socialProviders,  toggleSocialProvider,  updateSocialConfig,
    saveAll, resetAll, lastSaved,
  } = useIntegrations();

  const [activeTab,    setActiveTab]    = useState('meeting');
  const [expandedId,   setExpandedId]   = useState(null);
  const [showSecrets,  setShowSecrets]  = useState({});
  const [saved,        setSaved]        = useState(false);

  const toggleExpand = (id) => setExpandedId(prev => prev === id ? null : id);
  const toggleSecret = (id) => setShowSecrets(prev => ({ ...prev, [id]: !prev[id] }));

  const handleSave = () => {
    saveAll();
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  // ── Reusable key field ──────────────────────────────────────
  const KeyField = ({ label, value, onChange, isSecret, fieldId, placeholder, hint, todo }) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5 }}>
        <label style={{ fontSize:'.7rem', fontWeight:800, textTransform:'uppercase', letterSpacing:'.06em', color:'#374151' }}>{label}</label>
        {todo && <span style={{ background:'#fef3c7', color:'#92400e', fontSize:'.58rem', fontWeight:800, padding:'1px 6px', borderRadius:4 }}>TODO</span>}
      </div>
      <div style={{ position:'relative', display:'flex' }}>
        <input
          type={isSecret && !showSecrets[fieldId] ? 'password' : 'text'}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder || (todo ? 'Not configured — see README' : 'Enter value…')}
          style={{
            flex:1, padding:'9px 12px', paddingRight: isSecret ? 44 : 12,
            border:`1.5px solid ${value ? '#16a34a' : '#e5e7eb'}`,
            borderRadius:9, fontFamily:'monospace', fontSize:'.78rem', outline:'none',
            background: value ? '#f0fdf4' : '#f9fafb', color:'#111827',
          }}
        />
        {isSecret && (
          <button
            onClick={() => toggleSecret(fieldId)}
            style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#9ca3af', fontSize:'.8rem', padding:2 }}
          >
            {showSecrets[fieldId] ? '🙈' : '👁'}
          </button>
        )}
      </div>
      {hint && <div style={{ fontSize:'.65rem', color:'#9ca3af', marginTop:3 }}>{hint}</div>}
      {value && <div style={{ fontSize:'.65rem', color:'#16a34a', fontWeight:700, marginTop:3 }}>✅ Configured</div>}
    </div>
  );

  // ── Toggle switch ───────────────────────────────────────────
  const Toggle = ({ enabled, onToggle, label }) => (
    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
      <div
        onClick={onToggle}
        style={{
          width:46, height:26, borderRadius:100, cursor:'pointer',
          background: enabled ? '#16a34a' : '#e5e7eb',
          position:'relative', transition:'background .2s', flexShrink:0,
        }}
      >
        <div style={{
          position:'absolute', top:3,
          left: enabled ? 23 : 3,
          width:20, height:20, borderRadius:'50%', background:'#fff',
          boxShadow:'0 1px 4px rgba(0,0,0,.25)', transition:'left .2s',
        }} />
      </div>
      <span style={{ fontSize:'.82rem', fontWeight:700, color: enabled ? '#16a34a' : '#9ca3af' }}>
        {enabled ? 'Enabled' : 'Disabled'}
      </span>
    </div>
  );

  // ── Status badge ────────────────────────────────────────────
  const ConfigStatus = ({ items, keyField = 'clientId' }) => {
    const configured = items.filter(i => i.config?.[keyField]);
    return (
      <div style={{ display:'flex', gap:6, marginBottom:20, flexWrap:'wrap' }}>
        <div style={{ padding:'6px 12px', borderRadius:8, background:'#f0fdf4', border:'1px solid #bbf7d0', fontSize:'.72rem', fontWeight:700, color:'#16a34a' }}>
          ✅ {configured.length} Configured
        </div>
        <div style={{ padding:'6px 12px', borderRadius:8, background:'#fffbeb', border:'1px solid #fde68a', fontSize:'.72rem', fontWeight:700, color:'#92400e' }}>
          ⚙️ {items.length - configured.length} Need Setup
        </div>
        <div style={{ padding:'6px 12px', borderRadius:8, background:'#f0fdf4', border:'1px solid #bbf7d0', fontSize:'.72rem', fontWeight:700, color:'#16a34a' }}>
          ✅ {items.filter(i => i.enabled).length} Enabled
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 className="db-page-title">🔌 Integrations</h1>
          <p className="db-page-sub">Manage meeting platforms and social login providers. Enable/disable and configure API credentials.</p>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          {lastSaved && !saved && (
            <span style={{ fontSize:'.72rem', color:'#9ca3af' }}>
              Last saved {lastSaved.toLocaleTimeString()}
            </span>
          )}
          <button onClick={resetAll} style={{ padding:'8px 14px', borderRadius:9, border:'1.5px solid #e5e7eb', background:'#fff', fontFamily:'inherit', fontSize:'.78rem', fontWeight:700, color:'#6b7280', cursor:'pointer' }}>↺ Reset</button>
          <button onClick={handleSave} style={{ padding:'8px 18px', borderRadius:9, border:'none', background: saved ? '#16a34a' : '#1DB954', color:'#fff', fontFamily:'inherit', fontSize:'.82rem', fontWeight:800, cursor:'pointer', transition:'background .2s' }}>
            {saved ? '✅ Saved!' : '💾 Save Changes'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:0, border:'1.5px solid #e5e7eb', borderRadius:12, overflow:'hidden', marginBottom:24, width:'fit-content' }}>
        {[
          { id:'meeting', label:'📹 Meeting Platforms', count: meetingPlatforms.length },
          { id:'social',  label:'👤 Social Login',      count: socialProviders.length },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ padding:'10px 24px', border:'none', fontFamily:'inherit', fontSize:'.84rem', fontWeight:700, cursor:'pointer', transition:'all .15s',
              background: activeTab === t.id ? '#1DB954' : '#fff',
              color:      activeTab === t.id ? '#fff' : '#6b7280',
            }}>
            {t.label} <span style={{ marginLeft:4, background: activeTab === t.id ? 'rgba(255,255,255,.25)' : '#f3f4f6', padding:'1px 7px', borderRadius:100, fontSize:'.7rem' }}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════
          MEETING PLATFORMS TAB
          ══════════════════════════════════════════════ */}
      {activeTab === 'meeting' && (
        <div>
          <ConfigStatus items={meetingPlatforms} keyField="zoomSdkKey" />
          {meetingPlatforms.map(p => {
            const isOpen = expandedId === p.id;
            const hasConfig = Object.keys(p.config).length > 0;
            const configured = Object.values(p.config).some(v => v);
            return (
              <div key={p.id} className="db-card" style={{ marginBottom:14, border: p.enabled ? `1.5px solid ${p.color}20` : '1.5px solid #f3f4f6', transition:'border .2s' }}>
                {/* Platform row */}
                <div style={{ padding:'16px 20px', display:'flex', alignItems:'center', gap:14 }}>
                  {/* Icon + name */}
                  <div style={{ width:42, height:42, borderRadius:12, background:p.color, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <PlatformIcon id={p.id} size={22} color="#fff" />
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ fontWeight:800, fontSize:'.92rem', color:'#111827' }}>{p.label}</span>
                      {configured && <span style={{ background:'#f0fdf4', color:'#16a34a', fontSize:'.6rem', fontWeight:800, padding:'2px 7px', borderRadius:100 }}>🔑 Configured</span>}
                      {hasConfig && !configured && <span style={{ background:'#fef3c7', color:'#92400e', fontSize:'.6rem', fontWeight:800, padding:'2px 7px', borderRadius:100 }}>⚙️ Needs Setup</span>}
                    </div>
                    <div style={{ fontSize:'.72rem', color:'#9ca3af', marginTop:2 }}>{p.hint}</div>
                  </div>
                  {/* Toggle */}
                  <Toggle enabled={p.enabled} onToggle={() => toggleMeetingPlatform(p.id)} />
                  {/* Expand config */}
                  {hasConfig && (
                    <button onClick={() => toggleExpand(p.id)} style={{ padding:'7px 14px', borderRadius:8, border:'1.5px solid #e5e7eb', background:'#fff', cursor:'pointer', fontFamily:'inherit', fontSize:'.75rem', fontWeight:700, color:'#374151', display:'flex', alignItems:'center', gap:4 }}>
                      ⚙️ Config {isOpen ? '▲' : '▼'}
                    </button>
                  )}
                </div>

                {/* Expanded config section */}
                {isOpen && hasConfig && (
                  <div style={{ borderTop:'1px solid #f3f4f6', padding:'20px 20px 16px', background:'#fafafa' }}>
                    {/* Platform-specific config fields */}
                    {p.id === 'zoom' && (
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 20px' }}>
                        <KeyField label="Zoom SDK Key" value={p.config.zoomSdkKey} onChange={v => updateMeetingConfig('zoom', { zoomSdkKey: v })} fieldId="zoom_sdk_key" hint="From Zoom Marketplace → Meeting SDK App" todo={!p.config.zoomSdkKey} />
                        <KeyField label="Zoom SDK Secret" value={p.config.zoomSdkSecret} onChange={v => updateMeetingConfig('zoom', { zoomSdkSecret: v })} fieldId="zoom_sdk_secret" isSecret hint="⚠️ Used backend-only to sign signatures" todo={!p.config.zoomSdkSecret} />
                        <KeyField label="Zoom Account ID" value={p.config.zoomAccountId} onChange={v => updateMeetingConfig('zoom', { zoomAccountId: v })} fieldId="zoom_account_id" hint="For Server-to-Server OAuth (auto-create meetings)" todo={!p.config.zoomAccountId} />
                        <KeyField label="Zoom Client ID" value={p.config.zoomClientId} onChange={v => updateMeetingConfig('zoom', { zoomClientId: v })} fieldId="zoom_client_id" hint="From Zoom Marketplace → General OAuth App" todo={!p.config.zoomClientId} />
                        <KeyField label="Zoom Client Secret" value={p.config.zoomClientSecret} onChange={v => updateMeetingConfig('zoom', { zoomClientSecret: v })} fieldId="zoom_client_secret" isSecret hint="⚠️ Keep server-side only" todo={!p.config.zoomClientSecret} />
                        <div>
                          <label style={{ fontSize:'.7rem', fontWeight:800, textTransform:'uppercase', color:'#374151', display:'block', marginBottom:8 }}>SDK Embed</label>
                          <Toggle enabled={p.config.sdkEmbedEnabled} onToggle={() => updateMeetingConfig('zoom', { sdkEmbedEnabled: !p.config.sdkEmbedEnabled })} />
                          <div style={{ fontSize:'.65rem', color:'#9ca3af', marginTop:4 }}>Open Zoom inside DevBrains (requires SDK Key)</div>
                        </div>
                        <div>
                          <label style={{ fontSize:'.7rem', fontWeight:800, textTransform:'uppercase', color:'#374151', display:'block', marginBottom:8 }}>Auto-Create Meetings</label>
                          <Toggle enabled={p.config.autoCreateEnabled} onToggle={() => updateMeetingConfig('zoom', { autoCreateEnabled: !p.config.autoCreateEnabled })} />
                          <div style={{ fontSize:'.65rem', color:'#9ca3af', marginTop:4 }}>Auto-generate Zoom link on booking confirmation</div>
                        </div>
                      </div>
                    )}
                    {p.id === 'google_meet' && (
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 20px' }}>
                        <KeyField label="Google Client ID" value={p.config.googleClientId} onChange={v => updateMeetingConfig('google_meet', { googleClientId: v })} fieldId="gm_client_id" hint="From Google Cloud Console → OAuth 2.0 Credentials" todo={!p.config.googleClientId} />
                        <KeyField label="Google Client Secret" value={p.config.googleClientSecret} onChange={v => updateMeetingConfig('google_meet', { googleClientSecret: v })} fieldId="gm_client_secret" isSecret hint="⚠️ Keep server-side only" todo={!p.config.googleClientSecret} />
                        <div>
                          <label style={{ fontSize:'.7rem', fontWeight:800, textTransform:'uppercase', color:'#374151', display:'block', marginBottom:8 }}>Auto-Create Meet Links</label>
                          <Toggle enabled={p.config.autoCreateEnabled} onToggle={() => updateMeetingConfig('google_meet', { autoCreateEnabled: !p.config.autoCreateEnabled })} />
                          <div style={{ fontSize:'.65rem', color:'#9ca3af', marginTop:4 }}>Auto-generate Google Meet link on booking confirmation</div>
                        </div>
                      </div>
                    )}
                    {p.id === 'teams' && (
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 20px' }}>
                        <KeyField label="Azure Client ID" value={p.config.azureClientId} onChange={v => updateMeetingConfig('teams', { azureClientId: v })} fieldId="teams_client_id" hint="From Azure Portal → App registrations" todo={!p.config.azureClientId} />
                        <KeyField label="Azure Tenant ID" value={p.config.azureTenantId} onChange={v => updateMeetingConfig('teams', { azureTenantId: v })} fieldId="teams_tenant_id" hint="Your Azure AD Tenant ID" todo={!p.config.azureTenantId} />
                        <KeyField label="Azure Client Secret" value={p.config.azureClientSecret} onChange={v => updateMeetingConfig('teams', { azureClientSecret: v })} fieldId="teams_client_secret" isSecret hint="⚠️ Keep server-side only" todo={!p.config.azureClientSecret} />
                        <div>
                          <label style={{ fontSize:'.7rem', fontWeight:800, textTransform:'uppercase', color:'#374151', display:'block', marginBottom:8 }}>Auto-Create Meetings</label>
                          <Toggle enabled={p.config.autoCreateEnabled} onToggle={() => updateMeetingConfig('teams', { autoCreateEnabled: !p.config.autoCreateEnabled })} />
                          <div style={{ fontSize:'.65rem', color:'#9ca3af', marginTop:4 }}>Auto-generate Teams link on booking confirmation</div>
                        </div>
                      </div>
                    )}
                    {p.id === 'custom' && (
                      <div style={{ padding:'12px', background:'#f0fdf4', borderRadius:10, border:'1px solid #bbf7d0' }}>
                        <p style={{ margin:0, fontSize:'.8rem', color:'#166534' }}>✅ Custom links require no API credentials. Mentors paste any meeting URL directly.</p>
                      </div>
                    )}
                    <div style={{ marginTop:16, padding:'10px 14px', background:'#eff6ff', borderRadius:9, border:'1px solid #bfdbfe' }}>
                      <div style={{ fontSize:'.68rem', fontWeight:800, color:'#1e40af', marginBottom:3 }}>📖 Setup Instructions</div>
                      <div style={{ fontSize:'.72rem', color:'#1e40af' }}>See <code style={{ background:'#dbeafe', padding:'1px 5px', borderRadius:4 }}>MEETING_INTEGRATIONS.md §{p.id === 'google_meet' ? '1' : p.id === 'zoom' ? '2' : '3'}</code> for step-by-step setup guide.</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ══════════════════════════════════════════════
          SOCIAL LOGIN TAB
          ══════════════════════════════════════════════ */}
      {activeTab === 'social' && (
        <div>
          <ConfigStatus items={socialProviders} keyField="clientId" />
          {socialProviders.map(p => {
            const isOpen    = expandedId === `social_${p.id}`;
            const configured = !!(p.config.clientId);
            return (
              <div key={p.id} className="db-card" style={{ marginBottom:14, border: p.enabled ? `1.5px solid ${p.color}20` : '1.5px solid #f3f4f6', transition:'border .2s' }}>
                <div style={{ padding:'16px 20px', display:'flex', alignItems:'center', gap:14 }}>
                  {/* Icon */}
                  <div style={{ width:42, height:42, borderRadius:12, background:p.bgColor, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {p.id === 'google' && (
                      <svg width="22" height="22" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    )}
                    {p.id === 'github' && (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="#24292e">
                        <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/>
                      </svg>
                    )}
                    {p.id === 'linkedin' && (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="#0A66C2">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    )}
                  </div>

                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ fontWeight:800, fontSize:'.92rem', color:'#111827' }}>{p.label}</span>
                      {configured
                        ? <span style={{ background:'#f0fdf4', color:'#16a34a', fontSize:'.6rem', fontWeight:800, padding:'2px 7px', borderRadius:100 }}>🔑 Configured</span>
                        : <span style={{ background:'#fef3c7', color:'#92400e', fontSize:'.6rem', fontWeight:800, padding:'2px 7px', borderRadius:100 }}>⚙️ Needs Setup</span>
                      }
                    </div>
                    <div style={{ fontSize:'.72rem', color:'#9ca3af', marginTop:2 }}>{p.notes}</div>
                  </div>

                  <Toggle enabled={p.enabled} onToggle={() => toggleSocialProvider(p.id)} />

                  <button onClick={() => toggleExpand(`social_${p.id}`)} style={{ padding:'7px 14px', borderRadius:8, border:'1.5px solid #e5e7eb', background:'#fff', cursor:'pointer', fontFamily:'inherit', fontSize:'.75rem', fontWeight:700, color:'#374151', display:'flex', alignItems:'center', gap:4 }}>
                    🔑 Keys {isOpen ? '▲' : '▼'}
                  </button>
                </div>

                {/* Expanded key section */}
                {isOpen && (
                  <div style={{ borderTop:'1px solid #f3f4f6', padding:'20px 20px 16px', background:'#fafafa' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 20px' }}>
                      <KeyField
                        label="Client ID"
                        value={p.config.clientId}
                        onChange={v => updateSocialConfig(p.id, { clientId: v })}
                        fieldId={`${p.id}_client_id`}
                        hint={`From ${p.id === 'google' ? 'Google Cloud Console' : p.id === 'github' ? 'github.com/settings/developers' : 'linkedin.com/developers'}`}
                        todo={!p.config.clientId}
                      />
                      <KeyField
                        label="Client Secret"
                        value={p.config.clientSecret}
                        onChange={v => updateSocialConfig(p.id, { clientSecret: v })}
                        fieldId={`${p.id}_client_secret`}
                        isSecret
                        hint="⚠️ Keep secret — used backend-only"
                        todo={!p.config.clientSecret}
                      />
                    </div>

                    {/* Preview of what shows on login */}
                    <div style={{ marginTop:12, padding:'12px 16px', background:'#fff', borderRadius:10, border:'1px solid #e5e7eb' }}>
                      <div style={{ fontSize:'.7rem', fontWeight:800, color:'#374151', marginBottom:8 }}>👁 Login Modal Preview</div>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ padding:'8px 14px', border:`1.5px solid ${p.enabled ? p.color : '#e5e7eb'}`, borderRadius:9, background: p.enabled ? p.bgColor : '#f9fafb', fontSize:'.78rem', fontWeight:700, color: p.enabled ? p.color : '#9ca3af', display:'flex', alignItems:'center', gap:6, opacity: p.enabled ? 1 : 0.5 }}>
                          {p.id === 'google' && <svg width="14" height="14" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>}
                          {p.label}
                          {!p.enabled && <span style={{ fontSize:'.6rem', color:'#9ca3af' }}>(hidden)</span>}
                        </div>
                        <span style={{ fontSize:'.72rem', color: p.enabled ? '#16a34a' : '#ef4444', fontWeight:700 }}>
                          {p.enabled ? '✅ Visible on login/signup' : '✗ Hidden from login/signup'}
                        </span>
                      </div>
                    </div>

                    <div style={{ marginTop:14, padding:'10px 14px', background:'#eff6ff', borderRadius:9, border:'1px solid #bfdbfe' }}>
                      <div style={{ fontSize:'.68rem', fontWeight:800, color:'#1e40af', marginBottom:3 }}>📖 Setup Instructions</div>
                      <div style={{ fontSize:'.72rem', color:'#1e40af' }}>
                        See <code style={{ background:'#dbeafe', padding:'1px 5px', borderRadius:4 }}>SOCIAL_AUTH.md §{p.id === 'google' ? '1' : p.id === 'github' ? '2' : '3'}</code> for full step-by-step setup.
                        {' '}<a href={p.docsUrl} target="_blank" rel="noreferrer" style={{ color:'#2563eb', fontWeight:700 }}>Open developer console ↗</a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════
   CONTENT MANAGER — Admin edit Training + News
   ══════════════════════════════════════════ */
function ContentManager() {
  const {
    articles, news, CATEGORIES, NEWS_TYPES,
    addArticle, updateArticle, deleteArticle,
    addNews, updateNews, deleteNews,
    exportContent, resetToSeed,
  } = useContent();

  const [tab,      setTab]      = useState('articles');
  const [modal,    setModal]    = useState(null); // null | { mode:'new'|'edit', type:'article'|'news', data }
  const [search,   setSearch]   = useState('');
  const [delConfirm, setDelConfirm] = useState(null);

  // ── Filter ──────────────────────────────────────────────
  const filteredArticles = articles.filter(a =>
    !search || a.title.toLowerCase().includes(search.toLowerCase())
  ).sort((a,b) => b.id - a.id);

  const filteredNews = news.filter(n =>
    !search || n.title.toLowerCase().includes(n.title.toLowerCase())
  ).sort((a,b) => b.id - a.id);

  // ── Save handler ────────────────────────────────────────
  const handleSave = (formData) => {
    if (modal.type === 'article') {
      if (modal.mode === 'new') addArticle(formData);
      else updateArticle(modal.data.id, formData);
    } else {
      if (modal.mode === 'new') addNews(formData);
      else updateNews(modal.data.id, formData);
    }
    setModal(null);
  };

  const handleDelete = (id, type) => {
    if (type === 'article') deleteArticle(id);
    else deleteNews(id);
    setDelConfirm(null);
  };

  return (
    <div style={{ maxWidth:860 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 className="db-page-title">Content Manager</h1>
          <p className="db-page-sub">Manage training articles and platform news. Changes are saved instantly.</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={exportContent}
            style={{ padding:'8px 14px', borderRadius:9, border:'1.5px solid #e5e7eb', background:'#fff', fontFamily:'inherit', fontSize:'.78rem', fontWeight:700, cursor:'pointer', color:'#374151' }}>
            ⬇️ Export JSON
          </button>
          <button onClick={() => { if(window.confirm('Reset to seed content? This removes all admin edits.')) resetToSeed(); }}
            style={{ padding:'8px 14px', borderRadius:9, border:'1.5px solid #fecaca', background:'#fff', fontFamily:'inherit', fontSize:'.78rem', fontWeight:700, cursor:'pointer', color:'#dc2626' }}>
            🔄 Reset to Defaults
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:8, marginBottom:20 }}>
        {[['articles', `📚 Training Articles (${articles.length})`], ['news', `📢 Platform News (${news.length})`]].map(([k,l]) => (
          <button key={k} onClick={() => { setTab(k); setSearch(''); }}
            style={{ padding:'9px 18px', borderRadius:10, border:'none', fontFamily:'inherit', fontWeight:700, fontSize:'.84rem', cursor:'pointer',
              background: tab===k ? '#1a1a2e' : '#f3f4f6', color: tab===k ? '#fff' : '#6b7280' }}>
            {l}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
        <div style={{ flex:1, position:'relative', minWidth:200 }}>
          <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#9ca3af', fontSize:'.9rem', pointerEvents:'none' }}>🔍</span>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search..."
            style={{ width:'100%', padding:'9px 12px 9px 36px', border:'1.5px solid #e5e7eb', borderRadius:10, fontFamily:'inherit', fontSize:'.84rem', boxSizing:'border-box', outline:'none' }}/>
        </div>
        <button onClick={() => setModal({ mode:'new', type: tab==='articles'?'article':'news', data:{} })}
          style={{ padding:'9px 18px', borderRadius:10, border:'none', background:'#1DB954', color:'#fff', fontFamily:'inherit', fontWeight:800, fontSize:'.84rem', cursor:'pointer', flexShrink:0 }}>
          + New {tab==='articles'?'Article':'Announcement'}
        </button>
      </div>

      {/* ── Articles list ────────────────────────────── */}
      {tab === 'articles' && (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {filteredArticles.length === 0 && <div style={{ textAlign:'center', padding:'40px', color:'#9ca3af' }}>No articles found</div>}
          {filteredArticles.map(a => {
            const cat = CATEGORIES.find(c => c.id === a.category) || CATEGORIES[0];
            return (
              <div key={a.id} style={{ background:'#fff', border:'1.5px solid #e5e7eb', borderRadius:12, padding:'14px 18px', display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
                <span style={{ fontSize:'1.6rem', flexShrink:0 }}>{a.emoji || '📄'}</span>
                <div style={{ flex:1, minWidth:180 }}>
                  <div style={{ fontWeight:800, color:'#111827', fontSize:'.9rem', marginBottom:3 }}>{a.title}</div>
                  <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                    <span style={{ padding:'2px 8px', borderRadius:100, background:`${cat.color}18`, color:cat.color, fontSize:'.65rem', fontWeight:800 }}>{cat.icon} {cat.label}</span>
                    <span style={{ fontSize:'.7rem', color:'#9ca3af' }}>⏱ {a.readTimeMins} min</span>
                    <span style={{ fontSize:'.7rem', color:'#9ca3af' }}>{a.publishedAt}</span>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0, flexWrap:'wrap' }}>
                  {/* Published toggle */}
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <div onClick={() => updateArticle(a.id, { ...a, published:!a.published })}
                      style={{ width:42, height:24, borderRadius:100, cursor:'pointer', background:a.published?'#1DB954':'#d1d5db', position:'relative', transition:'background .2s' }}>
                      <div style={{ position:'absolute', top:3, left:a.published?21:3, width:18, height:18, borderRadius:'50%', background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,.2)', transition:'left .2s' }}/>
                    </div>
                    <span style={{ fontSize:'.7rem', fontWeight:700, color:a.published?'#16a34a':'#9ca3af' }}>{a.published?'Live':'Draft'}</span>
                  </div>
                  <button onClick={() => setModal({ mode:'edit', type:'article', data:a })}
                    style={{ padding:'6px 12px', borderRadius:8, border:'1.5px solid #e5e7eb', background:'#fff', fontFamily:'inherit', fontSize:'.75rem', fontWeight:700, cursor:'pointer', color:'#374151' }}>
                    ✏️ Edit
                  </button>
                  <button onClick={() => setDelConfirm({ id:a.id, type:'article', title:a.title })}
                    style={{ padding:'6px 10px', borderRadius:8, border:'1.5px solid #fecaca', background:'#fff', fontFamily:'inherit', fontSize:'.75rem', fontWeight:700, cursor:'pointer', color:'#dc2626' }}>
                    🗑
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── News list ─────────────────────────────────── */}
      {tab === 'news' && (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {filteredNews.length === 0 && <div style={{ textAlign:'center', padding:'40px', color:'#9ca3af' }}>No news items found</div>}
          {filteredNews.map(n => {
            const nt = NEWS_TYPES.find(t => t.id === n.newsType) || NEWS_TYPES[0];
            return (
              <div key={n.id} style={{ background:n.isPinned?'#fffdf5':'#fff', border:`1.5px solid ${n.isPinned?'#fde68a':'#e5e7eb'}`, borderRadius:12, padding:'14px 18px', display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
                <span style={{ fontSize:'1.6rem', flexShrink:0 }}>{n.emoji || '📢'}</span>
                <div style={{ flex:1, minWidth:180 }}>
                  <div style={{ fontWeight:800, color:'#111827', fontSize:'.9rem', marginBottom:3 }}>{n.title}</div>
                  <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                    <span style={{ padding:'2px 8px', borderRadius:100, background:nt.bg, color:nt.color, fontSize:'.65rem', fontWeight:800 }}>{nt.label}</span>
                    <span style={{ fontSize:'.7rem', color:'#9ca3af' }}>{n.date}</span>
                    {n.isPinned && <span style={{ fontSize:'.65rem', color:'#92400e', fontWeight:800 }}>📌 Pinned</span>}
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0, flexWrap:'wrap' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <div onClick={() => updateNews(n.id, { ...n, published:!n.published })}
                      style={{ width:42, height:24, borderRadius:100, cursor:'pointer', background:n.published?'#1DB954':'#d1d5db', position:'relative', transition:'background .2s' }}>
                      <div style={{ position:'absolute', top:3, left:n.published?21:3, width:18, height:18, borderRadius:'50%', background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,.2)', transition:'left .2s' }}/>
                    </div>
                    <span style={{ fontSize:'.7rem', fontWeight:700, color:n.published?'#16a34a':'#9ca3af' }}>{n.published?'Live':'Draft'}</span>
                  </div>
                  <button onClick={() => setModal({ mode:'edit', type:'news', data:n })}
                    style={{ padding:'6px 12px', borderRadius:8, border:'1.5px solid #e5e7eb', background:'#fff', fontFamily:'inherit', fontSize:'.75rem', fontWeight:700, cursor:'pointer', color:'#374151' }}>
                    ✏️ Edit
                  </button>
                  <button onClick={() => setDelConfirm({ id:n.id, type:'news', title:n.title })}
                    style={{ padding:'6px 10px', borderRadius:8, border:'1.5px solid #fecaca', background:'#fff', fontFamily:'inherit', fontSize:'.75rem', fontWeight:700, cursor:'pointer', color:'#dc2626' }}>
                    🗑
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Edit / New Modal ─────────────────────────── */}
      {modal && (
        <ContentEditModal
          modal={modal}
          CATEGORIES={CATEGORIES}
          NEWS_TYPES={NEWS_TYPES}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}

      {/* ── Delete confirm ────────────────────────────── */}
      {delConfirm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:28, maxWidth:400, width:'100%', boxShadow:'0 24px 60px rgba(0,0,0,.25)' }}>
            <div style={{ fontSize:'2rem', marginBottom:12 }}>🗑️</div>
            <div style={{ fontWeight:800, fontSize:'1rem', color:'#111827', marginBottom:8 }}>Delete this item?</div>
            <div style={{ fontSize:'.84rem', color:'#6b7280', marginBottom:20 }}>"{delConfirm.title}" will be permanently removed.</div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => setDelConfirm(null)}
                style={{ flex:1, padding:'10px', borderRadius:10, border:'1.5px solid #e5e7eb', background:'#fff', fontFamily:'inherit', fontWeight:700, cursor:'pointer', color:'#6b7280' }}>
                Cancel
              </button>
              <button onClick={() => handleDelete(delConfirm.id, delConfirm.type)}
                style={{ flex:1, padding:'10px', borderRadius:10, border:'none', background:'#ef4444', color:'#fff', fontFamily:'inherit', fontWeight:800, cursor:'pointer' }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Content Edit Modal ────────────────────────────────── */
function ContentEditModal({ modal, CATEGORIES, NEWS_TYPES, onSave, onClose }) {
  const isArticle = modal.type === 'article';
  const [form, setForm] = useState({
    title:      modal.data.title      || '',
    emoji:      modal.data.emoji      || (isArticle ? '📄' : '📢'),
    category:   modal.data.category   || CATEGORIES[0].id,
    newsType:   modal.data.newsType   || NEWS_TYPES[0].id,
    excerpt:    modal.data.excerpt    || '',
    summary:    modal.data.summary    || '',
    body:       modal.data.body       || '',
    publishedAt: modal.data.publishedAt || new Date().toISOString().split('T')[0],
    date:       modal.data.date       || new Date().toISOString().split('T')[0],
    published:  modal.data.published  ?? false,
    featured:   modal.data.featured   ?? false,
    isPinned:   modal.data.isPinned   ?? false,
  });
  const [error, setError] = useState('');

  const handle = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = () => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    if (!form.body.trim())  { setError('Body content is required.'); return; }
    onSave(form);
  };

  const inputStyle = {
    width:'100%', padding:'10px 12px', border:'1.5px solid #e5e7eb', borderRadius:10,
    fontFamily:'inherit', fontSize:'.86rem', boxSizing:'border-box', outline:'none',
  };
  const labelStyle = { display:'block', fontSize:'.72rem', fontWeight:700, color:'#374151', marginBottom:5 };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:9999, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'24px 16px', overflowY:'auto' }}>
      <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:640, boxShadow:'0 32px 80px rgba(0,0,0,.3)' }}>
        {/* Header */}
        <div style={{ padding:'18px 24px', background:'linear-gradient(135deg,#1a1a2e,#16213e)', borderRadius:'20px 20px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ color:'#fff', fontWeight:900, fontSize:'1rem' }}>
            {modal.mode === 'new' ? '+ New' : '✏️ Edit'} {isArticle ? 'Training Article' : 'News Item'}
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,.12)', border:'none', borderRadius:'50%', width:30, height:30, color:'#fff', cursor:'pointer', fontSize:'1rem', display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
        </div>

        {/* Form */}
        <div style={{ padding:'24px', display:'flex', flexDirection:'column', gap:16 }}>

          {/* Row: emoji + title */}
          <div style={{ display:'grid', gridTemplateColumns:'72px 1fr', gap:12 }}>
            <div>
              <label style={labelStyle}>Emoji</label>
              <input value={form.emoji} onChange={e=>handle('emoji',e.target.value)} maxLength={4}
                style={{ ...inputStyle, textAlign:'center', fontSize:'1.4rem', padding:'8px' }}/>
            </div>
            <div>
              <label style={labelStyle}>Title *</label>
              <input value={form.title} onChange={e=>handle('title',e.target.value)}
                placeholder={isArticle ? "How to Prepare for Your First Session" : "New Feature: Team Bookings"}
                style={inputStyle}/>
            </div>
          </div>

          {/* Category / Type */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            {isArticle ? (
              <>
                <div>
                  <label style={labelStyle}>Category</label>
                  <select value={form.category} onChange={e=>handle('category',e.target.value)} style={{ ...inputStyle, cursor:'pointer' }}>
                    {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Publish Date</label>
                  <input type="date" value={form.publishedAt} onChange={e=>handle('publishedAt',e.target.value)} style={inputStyle}/>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label style={labelStyle}>Type</label>
                  <select value={form.newsType} onChange={e=>handle('newsType',e.target.value)} style={{ ...inputStyle, cursor:'pointer' }}>
                    {NEWS_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Date</label>
                  <input type="date" value={form.date} onChange={e=>handle('date',e.target.value)} style={inputStyle}/>
                </div>
              </>
            )}
          </div>

          {/* Excerpt / Summary */}
          <div>
            <label style={labelStyle}>{isArticle ? 'Excerpt' : 'Summary'} <span style={{ color:'#9ca3af', fontWeight:400 }}>(shown on listing page)</span></label>
            <textarea value={isArticle ? form.excerpt : form.summary}
              onChange={e=>handle(isArticle?'excerpt':'summary', e.target.value)}
              rows={2} placeholder="A short summary shown on the listing page (160 chars recommended)..."
              style={{ ...inputStyle, resize:'vertical' }}/>
          </div>

          {/* Body */}
          <div>
            <label style={labelStyle}>Body (HTML) * <span style={{ color:'#9ca3af', fontWeight:400 }}>— use &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;strong&gt;, &lt;blockquote&gt;</span></label>
            <textarea value={form.body} onChange={e=>handle('body',e.target.value)}
              rows={10} placeholder="<h2>Section Title</h2><p>Content here...</p>"
              style={{ ...inputStyle, resize:'vertical', fontFamily:'monospace', fontSize:'.8rem', lineHeight:1.6 }}/>
          </div>

          {/* Toggles */}
          <div style={{ display:'flex', gap:24, flexWrap:'wrap' }}>
            {[
              { key:'published',  label:'Published (live on site)' },
              ...(isArticle ? [{ key:'featured', label:'Featured (show in highlights)' }] : [{ key:'isPinned', label:'Pinned (show at top)' }]),
            ].map(({ key, label }) => (
              <div key={key} style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}
                onClick={() => handle(key, !form[key])}>
                <div style={{ width:42, height:24, borderRadius:100, background:form[key]?'#1DB954':'#d1d5db', position:'relative', transition:'background .2s', flexShrink:0 }}>
                  <div style={{ position:'absolute', top:3, left:form[key]?21:3, width:18, height:18, borderRadius:'50%', background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,.2)', transition:'left .2s' }}/>
                </div>
                <span style={{ fontSize:'.82rem', fontWeight:600, color:'#374151' }}>{label}</span>
              </div>
            ))}
          </div>

          {error && <div style={{ padding:'9px 13px', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:9, fontSize:'.78rem', color:'#dc2626', fontWeight:600 }}>⚠️ {error}</div>}

          {/* Actions */}
          <div style={{ display:'flex', gap:10, paddingTop:8 }}>
            <button onClick={onClose}
              style={{ flex:1, padding:'12px', borderRadius:12, border:'1.5px solid #e5e7eb', background:'#fff', fontFamily:'inherit', fontWeight:700, cursor:'pointer', color:'#6b7280' }}>
              Cancel
            </button>
            <button onClick={handleSubmit}
              style={{ flex:2, padding:'12px', borderRadius:12, border:'none', background:'#1DB954', color:'#fff', fontFamily:'inherit', fontWeight:900, fontSize:'.9rem', cursor:'pointer' }}>
              💾 {modal.mode==='new' ? 'Create & Save' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Normalize raw Mentor docs (with populated userId object) into a flat shape
// that renderCard and decide() can consume safely.
// userId is always stored as a plain string so URL interpolation never produces [object Object].
function normalizePendingMentors(list) {
  return list.map(m => {
    const u = (typeof m.userId === 'object' && m.userId) ? m.userId : {};
    const firstName = u.firstName || m.firstName || '';
    const lastName  = u.lastName  || m.lastName  || '';
    // Extract plain string id — handles ObjectId, populated object, or plain string
    const userIdStr = (typeof u._id === 'object' ? String(u._id) : u._id)
                   || (typeof m.userId === 'string' ? m.userId : '')
                   || '';
    return {
      id:           String(m._id || m.id || ''),
      _id:          String(m._id || m.id || ''),
      userId:       userIdStr,               // always a string — safe to put in URL
      name:         `${firstName} ${lastName}`.trim() || m.name || 'Mentor',
      email:        u.email   || m.email   || '',
      role:         (u.headline || m.role || '').split('|')[0].trim() || 'Mentor',
      company:      m.company || (u.headline || '').split('|')[1]?.trim() || '',
      exp:          m.experience || m.exp || 0,
      skills:       m.skills  || [],
      avatarBg:     u.avatarBg || m.avatarBg || '4f46e5',
      initials:     m.initials || `${firstName[0]||'M'}${lastName[0]||''}`.toUpperCase(),
      avatarSeed:   u.avatar  || m.avatarSeed || firstName,
      submittedDate: m.submittedDate || (m.createdAt
        ? new Date(m.createdAt).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })
        : '—'),
      // isActive: undefined means not yet fetched; true = active, false = suspended
      isActive: u.isActive !== undefined ? u.isActive : m.isActive,
      _source: 'api',
    };
  });
}

export default function AdminDashboard() {
  const isMock = process.env.REACT_APP_MOCK !== 'false';
  const [stats,       setStats]       = useState(isMock ? (ADMIN_DASHBOARD.stats || {}) : {});
  const [adminLoading,setAdminLoading] = useState(!isMock);

  const [pendingMentors, setPendingMentors] = useState(() => {
    if (!isMock) return [];
    // Filter out already-acted entries so Overview never shows approved/rejected mentors
    try {
      const actedOn = JSON.parse(localStorage.getItem('db_approvals_acted') || '{}');
      return D.pendingMentors.filter(m => !actedOn[m.id]);
    } catch { return D.pendingMentors; }
  });
  const [recentBookings, setRecentBookings] = useState(isMock ? D.recentBookings : []);
  const [allUsers,       setAllUsers]       = useState(isMock ? D.allUsers       : []);

  const handleApprove = async (mentorId) => {
    if (isMock) {
      // Persist so Overview and Approvals page stay in sync after navigation
      try {
        const acted = JSON.parse(localStorage.getItem('db_approvals_acted') || '{}');
        acted[mentorId] = 'approved';
        localStorage.setItem('db_approvals_acted', JSON.stringify(acted));
      } catch {}
      setPendingMentors(p => p.filter(m => (m._id || m.id) !== mentorId));
      return;
    }
    // Use userId (User._id string) for the API — not the Mentor doc _id
    const item = pendingMentors.find(m => (m._id || m.id) === mentorId);
    const rawUid = item?.userId;
    const userIdForApi = (typeof rawUid === 'object' ? String(rawUid?._id || '') : rawUid) || mentorId;
    try{ await apiClient.put(`/admin/users/${userIdForApi}/approve`); setPendingMentors(p=>p.filter(m=>(m._id||m.id)!==mentorId)); }catch(e){ console.error(e); }
  };
  const handleReject = async (mentorId) => {
    if (isMock) {
      try {
        const acted = JSON.parse(localStorage.getItem('db_approvals_acted') || '{}');
        acted[mentorId] = 'rejected';
        localStorage.setItem('db_approvals_acted', JSON.stringify(acted));
      } catch {}
      setPendingMentors(p => p.filter(m => (m._id || m.id) !== mentorId));
      return;
    }
    const item = pendingMentors.find(m => (m._id || m.id) === mentorId);
    const rawUidR = item?.userId;
    const userIdForApiR = (typeof rawUidR === 'object' ? String(rawUidR?._id || '') : rawUidR) || mentorId;
    try{ await apiClient.put(`/admin/users/${userIdForApiR}/reject`); setPendingMentors(p=>p.filter(m=>(m._id||m.id)!==mentorId)); }catch(e){ console.error(e); }
  };

  useEffect(() => {
    if (isMock) return;
    if (!sessionStorage.getItem('db_user')) { setAdminLoading(false); return; }

    // Serve cache instantly
    const cached = getCached('admin_dashboard');
    if (cached) {
      if (cached.stats)          setStats(cached.stats);
      // Re-normalize cached pending in case it was stored before normalizePendingMentors existed
      if (cached.pending?.length) setPendingMentors(normalizePendingMentors(cached.pending));
      if (cached.bookings?.length) setRecentBookings(cached.bookings);
      if (cached.users?.length)   setAllUsers(cached.users);
      setAdminLoading(false);
    }

    apiClient.get('/admin/dashboard/stats')
      .then(res => {
        const raw = res.stats || {};
        // Normalize API stats keys to match mock platformStats shape
        const s = {
          ...raw,
          commissionEarned: raw.totalCommission || raw.commissionEarned || 0,
          monthCommission:  raw.monthCommission  || 0,
          completedBookings: raw.completedBookings || 0,
        };
        const pm  = res.pendingMentors || [];
        const rb  = res.recentBookings || [];
        const ru  = res.recentUsers    || [];
        setStats(s);
        console.log('[AdminDashboard] stats pendingMentors raw:', pm.length, pm[0]);
        const normalizedPm = normalizePendingMentors(pm);
        console.log('[AdminDashboard] normalizedPm:', normalizedPm.length, normalizedPm[0]);
        if (normalizedPm.length) setPendingMentors(normalizedPm);
        if (rb.length) setRecentBookings(rb);
        if (ru.length) setAllUsers(ru);
        // Store normalized data in cache so cache-load also gets clean userId strings
        setCached('admin_dashboard', { stats:s, pending:normalizedPm, bookings:rb, users:ru });
      })
      .catch(err => console.error('Admin stats error:', err))
      .finally(() => setAdminLoading(false));
  }, []); // eslint-disable-line


  const { pathname } = useLocation();
  const sectionMap = {
    '/dashboard/admin':'Overview',
    '/dashboard/admin/users':'Users',
    '/dashboard/admin/bookings':'Bookings',
    '/dashboard/admin/finance':'Finance',
    '/dashboard/admin/approvals':'Approvals',
    '/dashboard/admin/certifications':'Certifications',
    '/dashboard/admin/events':'Events',
    '/dashboard/admin/features':'Feature Flags',
    '/dashboard/admin/email':'Email',
    '/dashboard/admin/integrations':'Integrations',
    '/dashboard/admin/support':'Support',
    '/dashboard/admin/settings':'Settings',
    '/dashboard/admin/content':'Content',
  };
  const section = Object.entries(sectionMap).find(([k]) => k === pathname)?.[1] || 'Overview';
  if (adminLoading) {
    return (
      <DashboardLayout activeSection={section}>
        <DashboardSkeleton cols={4} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout activeSection={section}>
      <Routes>
        <Route index element={<Overview stats={stats} pendingMentors={pendingMentors} recentBookings={recentBookings} onApprove={handleApprove} onReject={handleReject}/>}/>
        <Route path="users" element={<Users users={allUsers}/>}/>
        <Route path="bookings" element={<Bookings bookings={recentBookings} stats={stats}/>}/>
        <Route path="finance" element={<Finance stats={stats}/>}/>
        <Route path="approvals" element={<Approvals initialPending={pendingMentors}/>}/>
        <Route path="certifications" element={<CertificationMgmt/>}/>
        <Route path="events" element={<EventsMgmt/>}/>
        <Route path="features" element={<FeatureFlags/>}/>
        <Route path="email" element={<EmailAdmin/>}/>
        <Route path="integrations" element={<Integrations/>}/>
        <Route path="support" element={<SupportAdmin/>}/>
        <Route path="settings" element={<Settings/>}/>
        <Route path="content" element={<ContentManager/>}/>
      </Routes>
    </DashboardLayout>
  );
}
